import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Link, useLocation, useNavigate, useOutletContext, useParams, useSearchParams,
} from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { ENTITIES, useEntityList, useEntityDoc, roleSatisfies, useTenant, isPrivileged, createEntity } from '../../data/entities';
import { auth } from '../../firebaseConfig';
import { uploadFile, buildKey, isUploadConfigured } from '../../data/uploadService';
import { DesignLinkSelect } from '../../components/InstanceLink';
import { useCan } from '../../data/permissions';
import { apiGet, apiPost, apiDelete, isBackendConfigured } from '../../utils/backendApi';
import Clickwrap, { OrgAgree } from '../../components/Clickwrap';
import { fetchAgreements } from '../../data/legal';
import './AppsModule.css';
import { APP_PLANS } from '../../data/plans';
import CheckoutIntent from '../../components/CheckoutIntent';
import MailSection from './MailSection';
import BudgetRequests from './BudgetRequests';
import EcosystemFrame from '../../components/three/EcosystemFrame';
import CommandView from '../../components/command/CommandView';
import useCommandToggle, { CommandToggle } from '../../components/command/useCommandToggle';
import { appPanels } from '../../components/command/appHud';
import OrgCommandView from '../../components/command/OrgCommandView';
import { TelemetryControls, ActivityStrip } from '../../components/command/useTelemetry';
import useAppCommand from '../../components/command/useAppCommand';
import { useAppWire } from '../../components/command/useAppLive';
import { trafficPanel, anomalyPanel, AnomalyTable, FlowTable } from '../../components/command/telemetryHud';
import { deriveCommand } from '../../components/three/commandState';
import { APP_ASPECT, appLive } from '../live/routes';

export { APP_PLANS };

// Apps module: org admins launch stamped instances of the foundation kit
// ("apps") — wizard (details → agreements → Stripe Checkout prepaying month
// 1), then a provisioner stamps a live instance at https://<slug>.apps.nventr.io.

const SLUG_RE = /^[a-z][a-z0-9-]{2,39}$/;
const REGIONS = ['us-east-1']; // only region offered for now

// App types: the starter template the instance boots with. The kit ships all
// of them; provision writes APP_TYPE into the instance runtime config and
// seeds matching demo content, so the first look is a real site — not the
// blank items demo. Keys must match the kit's template registry and the
// backend allowlist (live config apps.appTypes).
const VERDICT_COPY = {
  layer: 'This repository is already an app layer. We will host it as-is.',
  fork: 'This is a kit fork. We keep the extension files and leave edits outside those files behind.',
  foreign: 'This app is not on the nventr layer yet. The instance comes up as a fresh layer with a starter site; your repository is checked out read-only at ./source for the Dev Console to port from.',
};
// Three ways in (decision three-ways-in). The verdict above says what the
// tree IS; the intent says what the customer wants done with it.
const INTENT_COPY = {
  keep: 'Hosted as it is. Your repository is mirrored whole; the Dev Console, health checks and security scans work on it; nothing is rewritten.',
  rehash: 'Moved onto the App Kit. The instance repository becomes the application; the Dev Console ports your product into it.',
};
const verdictCopyOf = (v) => (v?.intent === 'keep' ? INTENT_COPY.keep : VERDICT_COPY[v?.klass] || v?.klass);
const RUNTIME_COPY = {
  container: 'nventr runs it: your Dockerfile is built on the instance host and served beside the console.',
  downstream: 'Your pipeline runs it: reviewed changes are pushed to your git; health and scans watch the URL that serves today.',
};

// Bring-an-existing-app Documents step: roadmap, change requests, specs.
// Each file becomes a knowledgeDoc draft (same upload + extract path as
// Knowledge) and the ids ride on the assessment until go-live links them to
// the builder app — the project brain starts from what they already wrote.
function BroughtDocsStep({ org, slug, docs, setDocs, onBack, onNext, busy }) {
  const [state, setState] = useState('');
  const [err, setErr] = useState('');
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setErr('');
    const added = [];
    for (const f of files) {
      try {
        setState(`Reading ${f.name}…`);
        let text = '';
        const ext = f.name.split('.').pop().toLowerCase();
        if (ext === 'pdf' || ext === 'docx') {
          if (!isUploadConfigured()) throw new Error('file uploads are not configured');
          const key = buildKey(`items/knowledge/${org.id}/`, f.name);
          const { key: usedKey } = await uploadFile(f, { key, contentType: f.type || 'application/octet-stream' });
          const r = await apiPost(`/knowledge/${org.id}/extract`, { key: usedKey, name: f.name, type: f.type, size: f.size });
          text = String(r.text || '');
        } else {
          text = await f.text();
        }
        if (!text.trim()) throw new Error(`${f.name} has no readable text`);
        const kind = /roadmap|backlog|plan/i.test(f.name) ? 'roadmap' : /change|request|cr-|ticket/i.test(f.name) ? 'change-requests' : 'spec';
        const id = await createEntity('knowledgeDoc', { org: org.id }, {
          title: f.name.replace(/\.[a-z0-9]+$/i, '').slice(0, 200),
          content: text.slice(0, 200000),
          sourceType: 'upload',
          sourceRef: '',
          audience: 'internal',
          tags: ['brought', kind],
          appIds: [],
          createdBy: auth.currentUser?.uid || '',
        });
        added.push({ id, name: f.name, kind, chars: text.length });
      } catch (ex) {
        setErr(ex.message);
      }
    }
    setState('');
    if (added.length) {
      const next = [...docs, ...added];
      setDocs(next);
      await apiPost('/apps/adoption/docs', { orgId: org.id, slug, docIds: next.map((d) => d.id) }).catch((ex) => setErr(ex.message));
    }
    e.target.value = '';
  };
  const remove = async (id) => {
    const next = docs.filter((d) => d.id !== id);
    setDocs(next);
    await apiPost('/apps/adoption/docs', { orgId: org.id, slug, docIds: next.map((d) => d.id) }).catch((ex) => setErr(ex.message));
  };
  return (
    <div className="ap-wizard-body">
      <p className="cs-help" style={{ marginTop: 0 }}>
        Attach what you already have: the roadmap, open change requests, specs, meeting notes. They become the first pages of this app&apos;s project brain, and the roadmap runner reads them to plan the work. The repository&apos;s own README and docs are read at go-live without any upload. Optional.
      </p>
      <div className="cs-field">
        <label>Documents — PDF, Word, markdown, text, CSV or JSON</label>
        <input type="file" multiple accept=".pdf,.docx,.txt,.md,.markdown,.csv,.json,text/*,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={onFiles} disabled={!!state} aria-label="Attach documents" />
        {state && <p className="cs-help">{state}</p>}
      </div>
      {docs.length > 0 && (
        <ul className="ap-brought-docs">
          {docs.map((d) => (
            <li key={d.id}>
              <code>{d.name}</code> <span className="cs-help">{d.kind} · {Math.round(d.chars / 1000)}k chars</span>
              <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => remove(d.id)} aria-label={`Remove ${d.name}`}>remove</button>
            </li>
          ))}
        </ul>
      )}
      {err && <p className="cs-help ap-slug-bad">{err}</p>}
      <div className="ap-wizard-foot">
        <button className="cs-btn cs-btn-outline" onClick={onBack} disabled={busy || !!state}>Back</button>
        <button className="cs-btn cs-btn-primary" onClick={onNext} disabled={busy || !!state}>
          {docs.length ? 'Continue' : 'Skip for now'}
        </button>
      </div>
    </div>
  );
}

export const APP_TYPES = [
  { key: 'blog', name: 'Content blog', blurb: 'Image, video, and long-form posts with a clean reading layout.' },
  { key: 'saas', name: 'SaaS company', blurb: 'Product hero, feature grid, pricing tiers, and a signup CTA.' },
  { key: 'commerce', name: 'Retail / ecommerce', blurb: 'Product catalog with detail pages and an order-inquiry flow.' },
  { key: 'institution', name: 'Institution', blurb: 'Funds, VCs, and banks — credibility, strategies, team, disclosures.' },
  { key: 'agency', name: 'Agency / portfolio', blurb: 'Case studies and portfolio work with a contact pipeline.' },
  { key: 'services', name: 'Local services', blurb: 'Offerings, hours, testimonials, and a booking-style contact page.' },
  { key: 'blank', name: 'Blank canvas', blurb: 'The bare foundation app — build your own front-of-house.' },
];

// Deployment targets: managed runs on the nventr fleet; the rest are
// customer-operated (sealed images + a per-target bootstrap script the
// customer downloads after checkout — the install kit).
export const DEPLOY_TARGETS = [
  { key: 'managed', name: 'nventr hosted', blurb: 'We run it at <slug>.apps.nventr.io — zero ops.' },
  { key: 'aws', name: 'Your AWS', blurb: 'Sealed images on an EC2 instance in your AWS account.' },
  { key: 'azure', name: 'Your Azure', blurb: 'Sealed images on a VM in your Azure subscription.' },
  { key: 'gcp', name: 'Your Google Cloud', blurb: 'Sealed images on a Compute Engine VM in your project.' },
  { key: 'onprem', name: 'On-prem', blurb: 'Sealed images on your own Linux server.' },
];

const STATUS_META = {
  draft: { label: 'Draft', cls: 'is-draft' },
  awaiting_payment: { label: 'Awaiting payment', cls: 'is-warn' },
  provisioning: { label: 'Provisioning', cls: 'is-info is-pulse' },
  'awaiting-install': { label: 'Awaiting install', cls: 'is-info is-pulse' },
  live: { label: 'Live', cls: 'is-ok' },
  suspended: { label: 'Suspended', cls: 'is-warn' },
  terminated: { label: 'Terminated', cls: 'is-muted' },
};

export function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status || 'unknown', cls: 'is-muted' };
  return <span className={`ap-badge ${meta.cls}`}>{meta.label}</span>;
}

const isByo = (app) => !!app?.deployment?.target && app.deployment.target !== 'managed';
const appUrl = (app) => app?.urls?.app
  || (app?.slug && !isByo(app) ? `https://${app.slug}.apps.nventr.io` : null);
// Display hostname: the provisioner's real URL when it exists (UAT plane
// instances live under apps-uat.*), else the managed default.
const appHost = (app) => (appUrl(app) || `${app?.slug || '—'}.apps.nventr.io`).replace(/^https?:\/\//, '');
const fmtUsd = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
};

// ---- Registry-first reads with a pinned-path fallback ----------------------
// appInstance / invoiceStatement / agreementAcceptance are registry entities,
// but the generated client registry (entityRegistry.gen.json) may lag behind
// the backend this wave. Until an entity lights up there, read the pinned
// collection path directly — rules stay the boundary either way.
export function useOrgDocs(entityName, fallbackCollection, orgId) {
  const registered = !!ENTITIES[entityName];
  const { docs: regDocs } = useEntityList(
    entityName, { org: orgId }, { orderBy: null, enabled: registered }
  );
  const [rawDocs, setRawDocs] = useState(null);
  useEffect(() => {
    if (registered || !orgId || !db) return undefined;
    const unsub = db.collection(`orgs/${orgId}/${fallbackCollection}`).onSnapshot(
      (snap) => setRawDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setRawDocs([])
    );
    return () => unsub();
  }, [registered, fallbackCollection, orgId]);
  return registered ? regDocs : rawDocs;
}

export function useOrgDoc(entityName, fallbackCollection, orgId, id) {
  const registered = !!ENTITIES[entityName];
  const { doc: regDoc } = useEntityDoc(entityName, { org: orgId }, registered ? id : null);
  const [rawDoc, setRawDoc] = useState(undefined);
  useEffect(() => {
    if (registered || !orgId || !id || !db) return undefined;
    const unsub = db.doc(`orgs/${orgId}/${fallbackCollection}/${id}`).onSnapshot(
      (snap) => setRawDoc(snap.exists ? { id: snap.id, ...snap.data() } : null),
      () => setRawDoc(null)
    );
    return () => unsub();
  }, [registered, fallbackCollection, orgId, id]);
  return registered ? regDoc : rawDoc;
}

export default function AppsModule() {
  const { org, myRole } = useOutletContext();
  const { appId } = useParams();
  if (appId) return <AppDetail org={org} appId={appId} myRole={myRole} />;
  return <AppsList org={org} myRole={myRole} />;
}

// ---- List + wizard ---------------------------------------------------------
function AppsList({ org, myRole }) {
  const apps = useOrgDocs('appInstance', 'apps', org.id); // scoped by TenantContext (selected workspace / my seats)
  const tenant = useTenant();
  const isAdmin = roleSatisfies(myRole, 'admin');
  const canEdit = useCan(org, myRole)('instance.create');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [bring, setBring] = useState(false);
  const cmd = useCommandToggle('org');
  const period = new Date().toISOString().slice(0, 7);
  const { doc: orgCost } = useEntityDoc('orgCost', { org: org.id }, isAdmin && cmd.on ? period : null);
  const { doc: orgUsage } = useEntityDoc('usage', { org: org.id }, cmd.on ? period : null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [checkoutMsg, setCheckoutMsg] = useState(null);

  // Returning from Stripe Checkout lands back on this route with query
  // params (?checkout=success|cancel&appId=…). Show a banner once, then
  // strip the params so refresh/back don't replay it.
  useEffect(() => {
    const c = searchParams.get('checkout');
    if (!c) return;
    setCheckoutMsg({ ok: c === 'success', appId: searchParams.get('appId') || '' });
    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    next.delete('appId');
    next.delete('session_id');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const sorted = useMemo(
    () => [...(apps || [])].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)),
    [apps]
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Hosted instances</h1>
        <span style={{ flex: 1 }} />
        {cmd.supported && apps && apps.length > 0 && <CommandToggle on={cmd.on} onChange={cmd.set} />}
        {canEdit && !wizardOpen && (
          <>
            <button className="cs-btn cs-btn-outline" onClick={() => { setBring(true); setWizardOpen(true); }}>
              Bring an existing app
            </button>
            <button className="cs-btn cs-btn-primary" onClick={() => { setBring(false); setWizardOpen(true); }}>
              New instance
            </button>
          </>
        )}
      </div>
      {!(cmd.on && apps && apps.length > 0) && (
        <p className="cs-help" style={{ marginTop: 6, maxWidth: 640 }}>
          Each hosted instance is a stamped copy of the nventr foundation with its own URL,
          database, Dev Console at /dev, managed hosting and metered monthly billing. An instance
          runs an app you design in App Builder — link them under App Builder → Surfaces.
          {tenant.workspace && <> Showing the <strong>{(tenant.workspaces || []).find((w) => w.id === tenant.workspace)?.name}</strong> workspace — change it in the sidebar.</>}
        </p>
      )}
      <CheckoutIntent />

      {checkoutMsg && (
        <div className={`ap-banner ${checkoutMsg.ok ? 'is-ok' : 'is-warn'}`}>
          {checkoutMsg.ok ? (
            <>
              Payment received — provisioning starts shortly.
              {checkoutMsg.appId && (
                <>
                  {' '}
                  <Link to={`/orgs/${org.id}/apps/${checkoutMsg.appId}`}>Watch progress →</Link>
                </>
              )}
            </>
          ) : (
            'Checkout was canceled — your app is saved as a draft; you can retry payment from its detail page.'
          )}
          <button className="ap-banner-x" onClick={() => setCheckoutMsg(null)} aria-label="Dismiss">✕</button>
        </div>
      )}

      {!isBackendConfigured() && (
        <p className="cs-help">Backend API not configured (REACT_APP_UPLOAD_API).</p>
      )}

      {wizardOpen && (
        <NewAppWizard org={org} bring={bring} onClose={() => setWizardOpen(false)} />
      )}

      {apps && !apps.length && !wizardOpen && (
        <div className="cs-empty" style={{ marginTop: 16 }}>
          No hosted instances yet{canEdit ? ' — launch your first one, or design the app first in App Builder.' : '.'}
        </div>
      )}
      {cmd.on && apps && apps.length > 0 && !wizardOpen && (
        <OrgCommandView org={org} apps={sorted} orgCost={orgCost} usage={orgUsage} isAdmin={isAdmin} chrome={checkoutMsg ? 230 : 180} />
      )}
      {!(cmd.on && apps && apps.length > 0 && !wizardOpen) && (
      <>
      <div className="ap-grid">
        {sorted.map((app) => (
          <Link key={app.id} to={`/orgs/${org.id}/apps/${app.id}`} className="ap-card">
            <div className="ap-card-head">
              <strong>{app.name || app.slug}</strong>
              <StatusBadge status={app.status} />
            </div>
            {app.status === 'live' && appUrl(app) ? (
              <a
                className="ap-card-url"
                href={appUrl(app)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {appUrl(app).replace(/^https?:\/\//, '')}
              </a>
            ) : (
              <span className="ap-card-url is-muted">
                {app.slug ? `${app.slug}.apps.nventr.io` : '—'}
              </span>
            )}
            <div className="ap-card-meta">
              <span>{APP_PLANS.find((p) => p.key === app.plan)?.name || app.plan || '—'}</span>
              {app.workspaceId && !tenant.workspace && <span title="Workspace">{(tenant.workspaces || []).find((w) => w.id === app.workspaceId)?.name || 'workspace'}</span>}
              {app.prepaidUntil && <span>prepaid until {fmtDate(app.prepaidUntil)}</span>}
            </div>
          </Link>
        ))}
      </div>

      {/* Environments: every prod app that has a UAT twin, side by side. */}
      {sorted.some((a) => a.uatAppId) && (
        <section className="ap-section">
          <h2>Environments</h2>
          {sorted.filter((a) => a.uatAppId && (a.envRole || 'prod') === 'prod').map((a) => {
            const twin = sorted.find((u) => u.id === a.uatAppId);
            return (
              <div key={a.id} className="ap-env-row">
                <div className="ap-env-row-head">
                  <Link to={`/orgs/${org.id}/apps/${a.id}`}><strong>{a.name || a.slug}</strong></Link>
                  <Link className="cs-help" to={`/orgs/${org.id}/apps/${a.id}`}>manage →</Link>
                </div>
                <EnvironmentsPanel org={org} app={a} uatDoc={twin} isAdmin={isAdmin} compact />
              </div>
            );
          })}
        </section>
      )}
      </>
      )}
    </div>
  );
}

// Four steps: details (name/slug/plan/region) → extras → agreements → review + pay.
// Bring-an-existing-app inserts repository + verdict before extras.
function NewAppWizard({ org, onClose, bring = false }) {
  const navigate = useNavigate();
  const tenant = useTenant();
  const [step, setStep] = useState(1);
  const steps = bring
    ? ['Details', 'Repository', 'Verdict', 'Documents', 'Extras', 'Agreements', 'Review & pay']
    : ['Details', 'Extras', 'Agreements', 'Review & pay'];
  const docsStep = 4;
  const extrasStep = bring ? 5 : 2;
  const agreeStep = bring ? 6 : 3;
  const reviewStep = bring ? 7 : 4;
  const [broughtDocs, setBroughtDocs] = useState([]);
  const [name, setName] = useState('');
  // Workspace the app lands in: the sidebar's selection by default; a plain
  // member must pick one of their seats (org-wide is an admin choice).
  const [workspaceId, setWorkspaceId] = useState(tenant.workspace || (isPrivileged(tenant.role) ? '' : (tenant.workspaces?.[0]?.id || '')));
  const [slug, setSlug] = useState('');
  const [plan, setPlan] = useState('starter');
  const [appType, setAppType] = useState('saas');
  const [target, setTarget] = useState('managed');
  const [byoKeys, setByoKeys] = useState(false);
  const [region, setRegion] = useState(REGIONS[0]);
  // Add-on catalog from live config (apps.priceBook.addons) + selection.
  const [extras, setExtras] = useState(null);
  const [kitIncluded, setKitIncluded] = useState(false);
  const [addons, setAddons] = useState({});
  // '', 'checking', 'available', 'taken', 'invalid'
  const [slugState, setSlugState] = useState('');
  const [agreements, setAgreements] = useState(null);
  const [agreementsErr, setAgreementsErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [repo, setRepo] = useState('');
  const [branch, setBranch] = useState('main');
  const [gitToken, setGitToken] = useState('');
  const [legacyUrl, setLegacyUrl] = useState('');
  const [builderAppId, setBuilderAppId] = useState('');
  const [verdict, setVerdict] = useState(null);
  // Three ways in: keep (host as is) / rehash (onto the kit); for keep, how
  // the app runs (detected from the Dockerfile, editable before continuing).
  const [intent, setIntent] = useState('rehash');
  const [runtime, setRuntime] = useState(null); // { kind, dockerfile, port, healthPath } | null = as detected
  const { docs: designs } = useEntityList('app', { org: org.id }, { enabled: bring, includeOrgWide: true });

  const slugValid = SLUG_RE.test(slug);

  const checkSlug = async () => {
    const s = slug.trim();
    if (!s) return;
    if (!SLUG_RE.test(s)) {
      setSlugState('invalid');
      return;
    }
    setSlugState('checking');
    try {
      await apiPost('/apps/reserve-slug', { orgId: org.id, slug: s });
      setSlugState('available');
    } catch (e) {
      if (e.status === 409) setSlugState('taken');
      else if (e.status === 400) setSlugState('invalid');
      else {
        setSlugState('');
        setError(e.message);
      }
    }
  };

  useEffect(() => {
    if (extras !== null) return undefined;
    let alive = true;
    apiGet(`/apps/pricebook/addons?orgId=${encodeURIComponent(org.id)}`)
      .then((out) => { if (alive) { setExtras(out?.addons || []); setKitIncluded(!!out?.kitIncluded); } })
      .catch(() => { if (alive) setExtras([]); });
    return () => { alive = false; };
  }, [extras, org.id]);

  // Agreements load when the user reaches step 3.
  useEffect(() => {
    if (step !== agreeStep || agreements !== null) return;
    let alive = true;
    fetchAgreements()
      .then((docs) => { if (alive) setAgreements(docs); })
      .catch((e) => { if (alive) setAgreementsErr(e.message); });
    return () => { alive = false; };
  }, [step, agreements, agreeStep]);

  const ORDER = ['tos', 'privacy', 'eula'];
  const orderedAgreements = (agreements || []).slice().sort(
    (a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind),
  );

  const proceed = async () => {
    setBusy(true);
    setError('');
    try {
      const { appId } = await apiPost('/apps', {
        orgId: org.id, name: name.trim(), slug: slug.trim(), plan, region, template: { appType, addons },
        deployment: { target: bring ? 'managed' : target, modelKeys: byoKeys ? 'byo' : 'hosted' },
        workspaceId: workspaceId || null,
        ...(bring ? { bring: true } : {}),
      });
      await apiPost(`/apps/${org.id}/${appId}/accept-agreements`, {
        docIds: agreements.map((d) => d.id),
        scrolled: true,
      });
      // Internal orgs skip Stripe — the backend provisions immediately and
      // returns { provisioned: true } instead of a checkout URL.
      const out = await apiPost(`/apps/${org.id}/${appId}/checkout`);
      if (out.provisioned) {
        navigate(`/orgs/${org.id}/apps/${appId}`);
        return;
      }
      window.location.href = out.url;
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  // The token stays in the form through the verdict step so a runtime
  // correction (port, health path) can re-run the check; it is dropped the
  // moment the step is left.
  const assessRepo = async (override = null) => {
    setBusy(true);
    setError('');
    try {
      const out = await apiPost('/apps/adoption/assess', {
        orgId: org.id,
        slug: slug.trim(),
        repo: repo.trim(),
        branch: branch.trim() || 'main',
        token: gitToken,
        legacyUrl: legacyUrl.trim(),
        builderAppId: builderAppId || null,
        intent,
        ...(override ? { runtime: override } : {}),
      });
      setVerdict(out);
      setRuntime(out.runtime || null);
      setStep(3);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const leaveVerdict = (next) => { setGitToken(''); setStep(next); };

  const acceptFork = async () => {
    setBusy(true);
    setError('');
    try {
      const out = await apiPost('/apps/adoption/accept', { orgId: org.id, slug: slug.trim() });
      setVerdict(out);
      leaveVerdict(docsStep);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const planCopy = APP_PLANS.find((p) => p.key === plan);
  const chosenExtras = (extras || []).filter((a) => addons[a.key]);
  const extrasCents = chosenExtras.reduce((s, a) => s + (a.feeCents || 0), 0);

  return (
    <div className="ap-wizard cs-card">
      <div className="ap-wizard-head">
        <div className="ap-stepper">
          {steps.map((label, i) => {
            const text = i === steps.length - 1 && kitIncluded ? 'Review' : label;
            return (
            <span
              key={label}
              className={`ap-step ${step === i + 1 ? 'is-active' : ''} ${step > i + 1 ? 'is-done' : ''}`}
            >
              <span className="ap-step-n">{step > i + 1 ? '✓' : i + 1}</span>
              {text}
            </span>
            );
          })}
        </div>
        <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>

      {step === 1 && (
        <div className="ap-wizard-body">
          <div className="cs-field">
            <label>Instance name</label>
            <input
              className="cs-input"
              placeholder="Acme Portal"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="cs-field">
            <label>Slug — your URL will be https://&lt;slug&gt;.apps.nventr.io</label>
            <input
              className="cs-input"
              placeholder="acme-portal"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value.toLowerCase());
                setSlugState('');
                setVerdict(null);
              }}
              onBlur={checkSlug}
            />
            {slug && !slugValid && (
              <p className="cs-help ap-slug-bad">
                3–40 chars, lowercase letters/digits/hyphens, must start with a letter.
              </p>
            )}
            {slugState === 'checking' && <p className="cs-help">Checking availability…</p>}
            {slugState === 'available' && <p className="cs-help ap-slug-ok">{slug} is available — reserved for you.</p>}
            {slugState === 'taken' && <p className="cs-help ap-slug-bad">{slug} is taken — try another.</p>}
            {slugState === 'invalid' && slugValid && <p className="cs-help ap-slug-bad">That slug isn&apos;t allowed.</p>}
          </div>
          {(tenant.workspaces || []).length > 0 && (
            <div className="cs-field">
              <label>Workspace — who in the organization can see and operate this app</label>
              <select className="cs-input" value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)} aria-label="Workspace">
                {isPrivileged(tenant.role) && <option value="">Org-wide (every member)</option>}
                {(tenant.workspaces || []).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
          )}
          <div className="cs-field">
            <label>What are you building? Your app launches with a matching starter site.</label>
            <div className="ap-plan-cards">
              {APP_TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`ap-plan-card ${appType === t.key ? 'is-selected' : ''}`}
                  onClick={() => setAppType(t.key)}
                >
                  <div className="ap-plan-head"><strong>{t.name}</strong></div>
                  <p>{t.blurb}</p>
                </button>
              ))}
            </div>
          </div>
          <div className="cs-field">
            <label>Plan</label>
            <div className="ap-plan-cards">
              {APP_PLANS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`ap-plan-card ${plan === p.key ? 'is-selected' : ''}`}
                  onClick={() => setPlan(p.key)}
                >
                  <div className="ap-plan-head">
                    <strong>{p.name}</strong>
                    <span className="ap-plan-price">{kitIncluded ? 'Included' : p.price}</span>
                  </div>
                  <p>{p.blurb}</p>
                  <ul>
                    {p.bullets.map((b) => <li key={b}>{b}</li>)}
                  </ul>
                </button>
              ))}
            </div>
            {kitIncluded && <p className="cs-help">This organization includes Kit instances. Pick the plan that fits; nothing is charged.</p>}
          </div>
          {!bring && <div className="cs-field">
            <label>Where will this run?</label>
            <div className="ap-plan-cards">
              {DEPLOY_TARGETS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`ap-plan-card ${target === t.key ? 'is-selected' : ''}`}
                  onClick={() => setTarget(t.key)}
                >
                  <div className="ap-plan-head"><strong>{t.name}</strong></div>
                  <p>{t.blurb}</p>
                </button>
              ))}
            </div>
            {target !== 'managed' && (
              <p className="cs-help">
                Customer-hosted: {kitIncluded ? 'when you confirm, you' : 'after payment you'} download an install kit
                (sealed images + a bootstrap script + your license key) and run
                it on your own server. The instance validates its license and
                reports usage statistics to nventr per your agreement.
              </p>
            )}
          </div>}
          {bring && (
            <p className="cs-help">This instance is hosted on nventr. The site you run today keeps serving until you cut the domain over.</p>
          )}
          <div className="cs-field">
            <label className="cs-check">
              <input type="checkbox" checked={byoKeys} onChange={(e) => setByoKeys(e.target.checked)} />
              {' '}Bring your own model keys
            </label>
            <p className="cs-help">
              {byoKeys
                ? 'You supply OpenAI / xAI keys on the instance. Hosted-token meters are dropped from your statement; voice and retrieval stay dark until your keys are in place.'
                : 'nventr provides the model keys; hosted tokens meter against your plan\u2019s included quota.'}
            </p>
          </div>
          <div className="cs-field" style={{ maxWidth: 260 }}>
            <label>Region</label>
            <select className="cs-input" value={region} onChange={(e) => setRegion(e.target.value)}>
              {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="ap-wizard-foot">
            <button
              className="cs-btn cs-btn-primary"
              disabled={!name.trim() || !slugValid || slugState !== 'available'}
              onClick={() => setStep(2)}
            >
              Continue
            </button>
            {slugValid && slugState !== 'available' && slugState !== 'checking' && (
              <span className="cs-help">Slug must be checked and available to continue.</span>
            )}
          </div>
        </div>
      )}

      {bring && step === 2 && (
        <div className="ap-wizard-body">
          <p className="cs-help" style={{ marginTop: 0 }}>
            Connect the GitHub repository. We read it to see how it should be hosted. The token is stored as a secret and is not kept in this form after the check.
          </p>
          <div className="cs-field">
            <label>Repository — owner/name</label>
            <input className="cs-input" placeholder="acme/portal" value={repo} onChange={(e) => setRepo(e.target.value)} />
          </div>
          <div className="cs-field">
            <label>Branch</label>
            <input className="cs-input" value={branch} onChange={(e) => setBranch(e.target.value)} />
          </div>
          <div className="cs-field">
            <label>Fine-grained read token</label>
            <input className="cs-input" type="password" autoComplete="off" value={gitToken} onChange={(e) => setGitToken(e.target.value)} />
          </div>
          <div className="cs-field">
            <label>URL that serves today</label>
            <input className="cs-input" placeholder="https://portal.example.com" value={legacyUrl} onChange={(e) => setLegacyUrl(e.target.value)} />
            <p className="cs-help">It keeps serving until you cut the domain over. The database does not move.</p>
          </div>
          <fieldset className="cs-field" data-nova-note="Three ways in: host the app as it is (its own stack, mirrored whole) or move it onto the App Kit (ported into the instance repository). A new app is the third way and does not come through here." style={{ border: 0, padding: 0, margin: '0 0 12px' }}>
            <legend style={{ padding: 0 }}>What should happen to it</legend>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 6 }}>
              <input type="radio" name="intent" value="keep" checked={intent === 'keep'} onChange={() => setIntent('keep')} />
              <span><strong>Host it as it is</strong><br /><span className="cs-help">{INTENT_COPY.keep}</span></span>
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 6 }}>
              <input type="radio" name="intent" value="rehash" checked={intent === 'rehash'} onChange={() => setIntent('rehash')} />
              <span><strong>Move it onto the App Kit</strong><br /><span className="cs-help">{INTENT_COPY.rehash}</span></span>
            </label>
          </fieldset>
          <div className="cs-field">
            <label>App Builder design — optional, or we create one when the instance is live</label>
            <select className="cs-input" value={builderAppId} onChange={(e) => setBuilderAppId(e.target.value)}>
              <option value="">Create one for this app</option>
              {(designs || []).filter((d) => !d.archived).map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          {error && <p className="cs-help ap-slug-bad">{error}</p>}
          <div className="ap-wizard-foot">
            <button className="cs-btn cs-btn-outline" onClick={() => setStep(1)} disabled={busy}>Back</button>
            <button className="cs-btn cs-btn-primary" disabled={busy || !repo.trim() || gitToken.length < 8} onClick={() => assessRepo()}>
              {busy ? 'Reading the repository…' : 'Check repository'}
            </button>
          </div>
        </div>
      )}

      {bring && step === 3 && !verdict && (
        <div className="ap-wizard-body">
          <p className="cs-help">Check the repository before continuing.</p>
          <div className="ap-wizard-foot">
            <button className="cs-btn cs-btn-outline" onClick={() => setStep(2)}>Back</button>
          </div>
        </div>
      )}

      {bring && step === 3 && verdict && (
        <div className="ap-wizard-body">
          <p style={{ marginTop: 0 }}><strong>{verdictCopyOf(verdict)}</strong></p>
          <p className="cs-help">
            {verdict.source?.name} @ {verdict.source?.branch}
            {verdict.legacyUrl ? ` · still serving ${verdict.legacyUrl}` : ''}
          </p>
          {intent === 'keep' && verdict.intent !== 'keep' && (
            <p className="cs-help">This repository is already shaped for the App Kit, so it is hosted on it; there is no separate stack of its own to keep.</p>
          )}
          {verdict.intent === 'keep' && (
            <div data-nova-note="How the brought app runs: nventr builds its Dockerfile into a container beside the console, or the customer's own pipeline keeps deploying it. Detected from the repository; the port and health path are editable here." style={{ margin: '8px 0 12px' }}>
              {(verdict.detected?.notes || []).map((n) => <p key={n} className="cs-help" style={{ margin: '0 0 4px' }}>{n}</p>)}
              <div className="cs-field" style={{ marginTop: 8 }}>
                <label>How it runs</label>
                <select className="cs-input" value={runtime?.kind || 'downstream'} onChange={(e) => setRuntime({ ...(runtime || {}), kind: e.target.value, dockerfile: runtime?.dockerfile || verdict.detected?.dockerfile || 'Dockerfile', port: runtime?.port || verdict.detected?.port || 3000, healthPath: runtime?.healthPath || '/', context: runtime?.context ?? verdict.detected?.context ?? '', buildArgs: runtime?.buildArgs ?? verdict.detected?.buildArgs ?? [], memory: runtime?.memory || verdict.detected?.memory || '1g' })}>
                  <option value="container">nventr runs it (your Dockerfile)</option>
                  <option value="downstream">Your pipeline runs it</option>
                </select>
                <p className="cs-help">{RUNTIME_COPY[runtime?.kind || 'downstream']}</p>
              </div>
              {runtime?.kind === 'container' && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <div className="cs-field" style={{ flex: '1 1 200px' }}>
                    <label>Dockerfile</label>
                    <input className="cs-input" list="adoption-dockerfiles" value={runtime.dockerfile || 'Dockerfile'} onChange={(e) => setRuntime({ ...runtime, dockerfile: e.target.value })} />
                    {(verdict.detected?.dockerfiles || []).length > 1 && (
                      <datalist id="adoption-dockerfiles">{verdict.detected.dockerfiles.map((d) => <option key={d} value={d} />)}</datalist>
                    )}
                  </div>
                  <div className="cs-field" style={{ width: 120 }}>
                    <label>Listens on</label>
                    <input className="cs-input" inputMode="numeric" value={runtime.port || ''} onChange={(e) => setRuntime({ ...runtime, port: e.target.value })} />
                  </div>
                  <div className="cs-field" style={{ flex: '1 1 160px' }}>
                    <label>Health path</label>
                    <input className="cs-input" value={runtime.healthPath || '/'} onChange={(e) => setRuntime({ ...runtime, healthPath: e.target.value })} />
                  </div>
                  <div className="cs-field" style={{ flex: '1 1 160px' }}>
                    <label>Build context</label>
                    <input className="cs-input" value={runtime.context ?? ''} onChange={(e) => setRuntime({ ...runtime, context: e.target.value })} placeholder="the Dockerfile's directory; . for the repository root" />
                  </div>
                  <div className="cs-field" style={{ flex: '2 1 260px' }}>
                    <label>Build args (names; values from the app env file)</label>
                    <input className="cs-input" value={Array.isArray(runtime.buildArgs) ? runtime.buildArgs.join(', ') : (runtime.buildArgs || '')} onChange={(e) => setRuntime({ ...runtime, buildArgs: e.target.value })} placeholder="NEXT_PUBLIC_API_URL, …" />
                  </div>
                  <div className="cs-field" style={{ width: 110 }}>
                    <label>Memory</label>
                    <select className="cs-input" value={runtime.memory || '1g'} onChange={(e) => setRuntime({ ...runtime, memory: e.target.value })}>
                      {['512m', '1g', '2g', '4g'].map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
              )}
              <p className="cs-help">
                It answers at <code>{slug.trim()}-app.apps.nventr.io</code> (UAT at <code>{slug.trim()}-app-uat.apps.nventr.io</code>) until you cut your domain over; the console stays at <code>{slug.trim()}.apps.nventr.io</code>. Its environment comes from the <code>app-env</code> secret in the Dev Console (<code>app-env-uat</code> for UAT); build args read their values there too. Databases and other services stay where they are.
              </p>
              {JSON.stringify(runtime) !== JSON.stringify(verdict.runtime) && (
                <button className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy || gitToken.length < 8} onClick={() => assessRepo(runtime)}>
                  {busy ? 'Saving…' : 'Save these settings'}
                </button>
              )}
            </div>
          )}
          {verdict.klass === 'fork' && (
            <>
              <p className="cs-help">
                These files sit outside the app layer, so they are not carried ({verdict.droppedCount || verdict.dropped?.length || 0}).
                Kit files that match the current kit are left behind with them.
              </p>
              {(verdict.dropped || []).length > 0 && (
                <ul>
                  {verdict.dropped.slice(0, 40).map((p) => <li key={p}><code>{p}</code></li>)}
                </ul>
              )}
              {(verdict.droppedCount || 0) > 40 && <p className="cs-help">and {verdict.droppedCount - 40} more</p>}
            </>
          )}
          {verdict.klass === 'foreign' && verdict.intent !== 'keep' && (
            <>
              <p className="cs-help">
                The instance comes up as a fresh layer with the starter site you picked. Your repository is not deployed as-is: the Dev Console has it checked out read-only at <code>./source</code> and ports what you ask for into the layer. Your site stays up until you cut over.
              </p>
              {(verdict.offenders || []).length > 0 && (
                <>
                  <p className="cs-help">Files outside the layer that made this a port, for example:</p>
                  <ul>
                    {verdict.offenders.map((p) => <li key={p}><code>{p}</code></li>)}
                  </ul>
                </>
              )}
            </>
          )}
          {error && <p className="cs-help ap-slug-bad">{error}</p>}
          <div className="ap-wizard-foot">
            <button className="cs-btn cs-btn-outline" onClick={() => setStep(2)} disabled={busy}>Back</button>
            {verdict.klass === 'fork' && verdict.state !== 'ready' ? (
              <button className="cs-btn cs-btn-primary" disabled={busy} onClick={acceptFork}>
                {busy ? 'Saving…' : 'These changes will not carry — continue'}
              </button>
            ) : (
              <button className="cs-btn cs-btn-primary" disabled={busy || (verdict.intent === 'keep' && JSON.stringify(runtime) !== JSON.stringify(verdict.runtime))} onClick={() => leaveVerdict(docsStep)}>Continue</button>
            )}
          </div>
        </div>
      )}

      {bring && step === docsStep && (
        <BroughtDocsStep org={org} slug={slug.trim()} docs={broughtDocs} setDocs={setBroughtDocs} busy={busy} onBack={() => setStep(3)} onNext={() => setStep(extrasStep)} />
      )}

      {step === extrasStep && (
        <div className="ap-wizard-body">
          <p className="cs-help" style={{ marginTop: 0 }}>
            Premium extras are billed monthly with the instance. Each one is
            wired in at stamp time — no separate setup.
          </p>
          {extras === null && <p className="cs-help">Loading extras…</p>}
          {extras && !extras.length && <p className="cs-help">No extras are offered right now.</p>}
          <div className="cs-card ap-included-note">
            <strong>Included: email on your domain.</strong>{' '}
            <span className="cs-help">
              Every Kit plan comes with mailboxes on your app's domain and a Gmail-style inbox inside the app at <code>/mail</code>, on nventr's shared mail service. Turn it on from the app page once it's live. The extra below is the single-tenant upgrade.
            </span>
          </div>
          <div className="ap-plan-cards">
            {(extras || []).map((a) => {
              const included = (a.includedPlans || []).includes(plan);
              return (
              <button
                key={a.key}
                type="button"
                className={`ap-plan-card ${addons[a.key] || included ? 'is-selected' : ''}`}
                onClick={() => { if (!included) setAddons((prev) => ({ ...prev, [a.key]: !prev[a.key] })); }}
                aria-pressed={included || !!addons[a.key]}
                disabled={included}
                style={included ? { cursor: 'default', opacity: 1 } : undefined}
              >
                <div className="ap-plan-head">
                  <strong>{a.label}</strong>
                  <span className="ap-plan-price">{included ? `Included${a.includedUsd ? ` · $${a.includedUsd}/mo of voice free` : ''}` : `+${fmtUsd(a.feeCents)}/mo`}</span>
                </div>
                <p>{a.blurb}</p>
                {a.key === 'uat' && <ul><li>Second instance at {slug || '<slug>'}-uat, provisioned right after prod goes live</li></ul>}
                {a.key === 'voice' && <ul><li>Voice + chat agent enabled in the app and Dev Console{included ? ' — on every paid Kit plan, overage billed per session' : ''}</li></ul>}
                {a.key === 'assurance' && <ul><li>Monthly passive security baseline + browser feature checks</li></ul>}
                {a.key === 'mailDedicated' && <ul><li>Own mail server + IP (no shared reputation) · needs your custom domain</li></ul>}
              </button>
              );
            })}
          </div>
          <div className="ap-wizard-foot">
            <button className="cs-btn cs-btn-outline" onClick={() => setStep(bring ? 3 : 1)}>Back</button>
            <button className="cs-btn cs-btn-primary" onClick={() => setStep(agreeStep)}>
              {chosenExtras.length ? `Continue with ${chosenExtras.length} extra${chosenExtras.length > 1 ? 's' : ''}` : 'Continue without extras'}
            </button>
          </div>
        </div>
      )}

      {step === agreeStep && (
        <div className="ap-wizard-body">
          {agreementsErr && <p className="cs-help ap-slug-bad">Couldn&apos;t load agreements: {agreementsErr}</p>}
          {agreements === null && !agreementsErr && <p className="cs-help">Loading agreements…</p>}
          {agreements && !agreements.length && (
            <p className="cs-help">No agreements are available right now.</p>
          )}
          {orderedAgreements.length > 0 && (
            <Clickwrap
              docs={orderedAgreements}
              intro="Read to the end, then agree on behalf of the organization."
              onAgree={() => setStep(reviewStep)}
            />
          )}
          <div className="ap-wizard-foot">
            <button className="cs-btn cs-btn-outline" onClick={() => setStep(extrasStep)}>Back</button>
          </div>
        </div>
      )}

      {step === reviewStep && (
        <div className="ap-wizard-body">
          <div className="ap-review">
            <div><span className="cs-help">Name</span><strong>{name}</strong></div>
            <div><span className="cs-help">URL</span><strong>https://{slug}.apps.nventr.io</strong></div>
            <div><span className="cs-help">Starter site</span><strong>{APP_TYPES.find((t) => t.key === appType)?.name}</strong></div>
            <div><span className="cs-help">Plan</span><strong>{planCopy?.name} · {kitIncluded ? 'Included' : planCopy?.price}</strong></div>
            <div>
              <span className="cs-help">Extras</span>
              <strong>
                {chosenExtras.length
                  ? `${chosenExtras.map((a) => a.label).join(', ')} · +${fmtUsd(extrasCents)}/mo`
                  : 'None'}
              </strong>
            </div>
            <div><span className="cs-help">Deployment</span><strong>{bring ? 'Hosted on nventr' : DEPLOY_TARGETS.find((t) => t.key === target)?.name}</strong></div>
            {bring && verdict && (
              <div><span className="cs-help">Repository</span><strong>{verdict.source?.name} · {verdictCopyOf(verdict)}</strong></div>
            )}
            {bring && verdict?.intent === 'keep' && (
              <div><span className="cs-help">Runs as</span><strong>{verdict.runtime?.kind === 'container' ? `your container at ${slug.trim()}-app.apps.nventr.io (port ${verdict.runtime.port}, health ${verdict.runtime.healthPath})` : 'your pipeline — reviewed changes are pushed to your git'}</strong></div>
            )}
            <div><span className="cs-help">Region</span><strong>{region}</strong></div>
            <div><span className="cs-help">Agreements</span><strong>{(agreements || []).length} accepted</strong></div>
          </div>
          <p className="cs-help">
            {kitIncluded
              ? 'Nothing is charged. Provisioning starts when you confirm.'
              : target === 'managed'
                ? 'You’ll be redirected to Stripe to prepay the first month. Provisioning starts automatically after payment.'
                : 'You’ll be redirected to Stripe to prepay the first month. After payment your app page offers the install kit for your server.'}
          </p>
          {error && <p className="cs-help ap-slug-bad">{error}</p>}
          <div className="ap-wizard-foot">
            <button className="cs-btn cs-btn-outline" onClick={() => setStep(agreeStep)} disabled={busy}>Back</button>
            <button className="cs-btn cs-btn-primary" disabled={busy} onClick={proceed}>
              {busy ? (kitIncluded ? 'Starting…' : 'Redirecting to Stripe…') : (kitIncluded ? 'Create instance' : 'Proceed to payment')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Instance detail --------------------------------------------------------
const STAGE_DONE = ['done', 'complete', 'completed', 'ok', 'success'];
const STAGE_RUN = ['running', 'active', 'in_progress', 'started'];
const STAGE_ERR = ['error', 'failed'];

function StageIcon({ status }) {
  const s = String(status || '').toLowerCase();
  if (STAGE_DONE.includes(s)) return <span className="ap-stage-ic is-ok">✓</span>;
  if (STAGE_RUN.includes(s)) return <span className="ap-stage-ic is-run" aria-label="running" />;
  if (STAGE_ERR.includes(s)) return <span className="ap-stage-ic is-err">✕</span>;
  return <span className="ap-stage-ic is-idle">○</span>;
}

// Uptime / latency / TLS for a live instance — rendered from the monitor
// worker's rollups via GET /apps/:orgId/:appId/health. Read-only.
export function HealthSection({ orgId, appId, canEdit }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [draft, setDraft] = useState({ name: '', path: '/', kind: 'http', expectStatus: '200', expectText: '', selector: '' });
  const [formErr, setFormErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => apiGet(`/apps/${orgId}/${appId}/health`)
    .then((d) => { setData(d); setErr(''); })
    .catch((e) => setErr(e.message));

  useEffect(() => {
    let alive = true;
    const pull = () => apiGet(`/apps/${orgId}/${appId}/health`)
      .then((d) => { if (alive) { setData(d); setErr(''); } })
      .catch((e) => { if (alive) setErr(e.message); });
    pull();
    const t = setInterval(pull, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, [orgId, appId]);

  const addCheck = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFormErr('');
    try {
      await apiPost(`/apps/${orgId}/${appId}/feature-checks`, {
        name: draft.name,
        path: draft.path,
        kind: draft.kind,
        expectStatus: draft.expectStatus === '' ? undefined : Number(draft.expectStatus),
        expectText: draft.expectText,
        selector: draft.selector,
      });
      setDraft({ name: '', path: '/', kind: 'http', expectStatus: '200', expectText: '', selector: '' });
      await load();
    } catch (e2) {
      setFormErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  const disableCheck = async (id) => {
    setFormErr('');
    try {
      await apiDelete(`/apps/${orgId}/${appId}/feature-checks/${id}`);
      await load();
    } catch (e2) {
      setFormErr(e2.message);
    }
  };

  if (err) return <p className="cs-help">Health data unavailable: {err}</p>;
  if (!data) return <p className="cs-help">Loading health…</p>;

  const m = data.monitor || {};
  const rollups = data.rollups || [];
  const totals = rollups.reduce((acc, r) => ({ total: acc.total + (r.total || 0), fails: acc.fails + (r.fails || 0) }), { total: 0, fails: 0 });
  const uptimePct = totals.total ? (100 * (totals.total - totals.fails)) / totals.total : null;
  const samples = rollups.flatMap((r) => r.samples || []).sort((a, b) => a - b);
  const pct = (p) => (samples.length ? samples[Math.min(samples.length - 1, Math.floor((p / 100) * samples.length))] : null);
  const checks = [...(data.checks || [])].reverse(); // oldest → newest
  const checksSummary = summarizeChecks(data.features || []);

  return (
    <>
      {!data.monitor && (
        <p className="cs-help">No service checks recorded yet — monitoring starts within a couple of minutes of going live.</p>
      )}
      <div className="ap-health-stats">
        <div className={`ap-health-stat ${m.state === 'up' ? 'is-ok' : m.state === 'down' ? 'is-down' : ''}`}>
          <span className={`ap-badge ${m.state === 'up' ? 'is-ok' : m.state === 'down' ? 'is-err' : 'is-muted'}`}>{m.state || 'unknown'}</span>
          <span className="cs-help">{m.lastCheckAt ? `checked ${fmtAgo(m.lastCheckAt)}` : 'now'}</span>
        </div>
        <div className={`ap-health-stat ${uptimePct == null ? '' : uptimePct >= 99.9 ? 'is-ok' : uptimePct >= 99 ? 'is-warn' : 'is-down'}`}><strong>{uptimePct != null ? `${uptimePct.toFixed(2)}%` : '—'}</strong><span className="cs-help">uptime · {rollups.length}d</span></div>
        <div className="ap-health-stat"><strong>{pct(50) != null ? `${pct(50)} ms` : '—'}</strong><span className="cs-help">p50 latency</span></div>
        <div className="ap-health-stat"><strong>{pct(95) != null ? `${pct(95)} ms` : '—'}</strong><span className="cs-help">p95 latency</span></div>
        <div className={`ap-health-stat ${m.tlsDaysLeft == null ? '' : m.tlsDaysLeft < 7 ? 'is-down' : m.tlsDaysLeft < 14 ? 'is-warn' : 'is-ok'}`}><strong>{m.tlsDaysLeft != null ? `${m.tlsDaysLeft} d` : '—'}</strong><span className="cs-help">TLS certificate</span></div>
        <div className={`ap-health-stat ${checksSummary.total ? (checksSummary.failing ? 'is-down' : 'is-ok') : ''}`}><strong>{checksSummary.total ? `${checksSummary.passing}/${checksSummary.total}` : '—'}</strong><span className="cs-help">feature checks passing</span></div>
        <div className={`ap-health-stat ${m.mcp === 'up' ? 'is-ok' : m.mcp === 'down' ? 'is-down' : m.mcp === 'unsupported' ? 'is-warn' : ''}`} title={m.mcpError || ''}>
          <strong>{m.mcp === 'up' ? 'ready' : m.mcp === 'down' ? 'down' : m.mcp === 'unsupported' ? 'needs kit update' : '—'}</strong>
          <span className="cs-help">MCP server · AI clients</span>
        </div>
      </div>
      {checks.length > 0 && (
        <div className="ap-health-dots" title="Recent checks (oldest → newest)">
          {checks.map((c, i) => (
            <span key={i} className={`ap-health-dot ${c.ok ? 'is-ok' : 'is-err'}`} title={`${new Date(c.ts).toLocaleTimeString()} · ${c.ok ? `${c.healthMs ?? c.rootMs ?? '—'} ms` : 'failed'}`} />
          ))}
        </div>
      )}
      {(data.remediations || []).length > 0 && (
        <div className="ap-remediations">
          <h4>Acted on its own</h4>
          <ul>
            {data.remediations.map((r) => (
              <li key={r.id}>
                <span className={`ap-badge ${r.ok ? 'is-ok' : 'is-err'}`}>{r.kind === 'rollback' ? 'rolled back' : `restarted ${r.service}`}</span>
                <span className="cs-help">{fmtAgo(r.ts)} · {r.reason}{r.kind === 'rollback' && r.toSha ? ` → ${String(r.toSha).slice(0, 7)}` : ''}{!r.ok && r.error ? ` · ${r.error}` : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <FeatureChecks
        features={data.features || []}
        assurance={data.assurance}
        canEdit={canEdit}
        draft={draft}
        setDraft={setDraft}
        formErr={formErr}
        busy={busy}
        onAdd={addCheck}
        onDisable={disableCheck}
      />
    </>
  );
}

function scanCountLine(scan) {
  const c = scan?.counts || {};
  const open = ['critical', 'high', 'medium', 'low', 'info']
    .filter((k) => c[k])
    .map((k) => `${c[k]} ${k}`);
  return open.length ? open.join(', ') : 'none open';
}

function summarizeChecks(features) {
  const active = (features || []).filter((f) => f.enabled);
  const ran = active.filter((f) => f.lastAt);
  const passing = ran.filter((f) => f.lastOk).length;
  return { total: active.length, ran: ran.length, passing, failing: ran.length - passing };
}

export function fmtAgo(ts) {
  if (!ts) return '—';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

function FeatureChecks({
  features, assurance, canEdit, draft, setDraft, formErr, busy, onAdd, onDisable,
}) {
  const active = features.filter((f) => f.enabled);
  return (
    <div style={{ marginTop: 18 }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>Feature checks</h3>
      <p className="cs-help">
        Your own “is this page still working?” checks, run against this instance every 15 minutes.
        An HTTP check hits a path and expects a status (and optional text); a browser check opens the page
        in a real browser and waits for an element.
        {assurance ? '' : ' Browser checks are part of the Assurance add-on.'}
      </p>
      {!active.length && <p className="cs-help">No checks yet{canEdit ? ' — add one below, e.g. “/login returns 200”.' : '.'}</p>}
      {active.length > 0 && (
        <table className="ap-checks">
          <thead>
            <tr><th>Check</th><th>Kind</th><th>Path</th><th>Last result</th><th>When</th>{canEdit && <th />}</tr>
          </thead>
          <tbody>
            {active.map((f) => (
              <tr key={f.id}>
                <td className="ap-checks-name">{f.name}</td>
                <td><span className="ap-checks-kind">{f.kind === 'browser' ? 'browser' : 'http'}</span></td>
                <td className="ap-mono">{f.path}{f.kind === 'browser' && f.selector ? ` → ${f.selector}` : f.expectStatus ? ` → ${f.expectStatus}` : ''}</td>
                <td>
                  {!f.lastAt ? <span className="cs-help" style={{ margin: 0 }}>not run yet</span>
                    : f.lastOk ? <span><i className="ap-dot is-ok" /> passing{f.lastStatus ? ` · ${f.lastStatus}` : ''}</span>
                      : <span><i className="ap-dot is-down" /> failing{f.lastError ? <div className="ap-checks-err">{f.lastError}</div> : null}</span>}
                </td>
                <td className="ap-mono">{f.lastAt ? fmtAgo(f.lastAt) : '—'}</td>
                {canEdit && (
                  <td>
                    <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => onDisable(f.id)} data-agent-noclick>Disable</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {canEdit && (
        <form onSubmit={onAdd} className="ap-check-form">
          <input className="cs-input" placeholder="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required />
          <input className="cs-input" placeholder="Path, like /ready" value={draft.path} onChange={(e) => setDraft({ ...draft, path: e.target.value })} required />
          <select className="cs-input" value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>
            <option value="http">HTTP</option>
            <option value="browser" disabled={!assurance}>Browser (Assurance)</option>
          </select>
          {draft.kind === 'http' && (
            <>
              <input className="cs-input" placeholder="Expected status" value={draft.expectStatus} onChange={(e) => setDraft({ ...draft, expectStatus: e.target.value })} />
              <input className="cs-input" placeholder="Expected text (optional)" value={draft.expectText} onChange={(e) => setDraft({ ...draft, expectText: e.target.value })} />
            </>
          )}
          {draft.kind === 'browser' && (
            <input className="cs-input" placeholder="CSS selector, like form" value={draft.selector} onChange={(e) => setDraft({ ...draft, selector: e.target.value })} required />
          )}
          <button className="cs-btn cs-btn-primary cs-btn-sm" type="submit" disabled={busy}>Add check</button>
          {formErr && <p className="cs-help ap-slug-bad" style={{ gridColumn: '1 / -1' }}>{formErr}</p>}
        </form>
      )}
      {!canEdit && formErr && <p className="cs-help ap-slug-bad">{formErr}</p>}
    </div>
  );
}

const SEV_ORDER = ['critical', 'high', 'medium', 'low', 'info'];
const SCANNER_LABEL = { 'zap-baseline': 'ZAP (web app)', nuclei: 'Nuclei (known CVEs)', testssl: 'TLS (testssl)' };

// Security posture: findings from the passive baseline (ZAP, nuclei, testssl)
// and browser feature checks — the Assurance add-on. Admins on Assurance can
// run a passive baseline on demand; active scans stay operator-only.
export function SecuritySection({ orgId, appId, isAdmin }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    let alive = true;
    const pull = () => apiGet(`/apps/${orgId}/${appId}/security`)
      .then((d) => { if (alive) { setData(d); setErr(''); } })
      .catch((e) => { if (alive) setErr(e.message); });
    pull();
    const t = setInterval(pull, 30_000);
    return () => { alive = false; clearInterval(t); };
  }, [orgId, appId]);

  const runScan = async () => {
    setBusy(true); setMsg('');
    try {
      const out = await apiPost(`/apps/${orgId}/${appId}/scan`);
      setMsg(`Baseline scan queued (${out.jobId}). Results land here as each scanner finishes — usually 5–15 minutes.`);
      const d = await apiGet(`/apps/${orgId}/${appId}/security`);
      setData(d);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  if (err) return <p className="cs-help">Security data unavailable: {err}</p>;
  if (!data) return <p className="cs-help">Loading security posture…</p>;

  const findings = data.findings || [];
  const open = findings.filter((f) => f.status === 'open');
  const resolved = findings.length - open.length;
  const counts = Object.fromEntries(SEV_ORDER.map((k) => [k, open.filter((f) => f.severity === k).length]));
  const jobs = data.jobs || [];
  const last = jobs[0] || null;
  const inFlight = last && ['queued', 'running'].includes(last.status);
  const lastDone = jobs.find((j) => ['done', 'error'].includes(j.status));
  const stageDot = (st) => (st?.status === 'done' ? 'is-ok' : st?.status === 'error' ? 'is-down' : st?.status === 'running' ? 'is-warn' : '');

  return (
    <>
      <p className="cs-help" style={{ marginTop: 0 }}><Link to={`/orgs/${orgId}/apps/${appId}/live/security`}>Open the full security page</Link> — scanners, run history, the findings ledger, and how this app is thresholded.</p>
      {!data.assurance && (
        <p className="cs-help">Security scanning is part of the Assurance add-on: a monthly passive baseline (OWASP ZAP, Nuclei CVE templates, TLS configuration) with findings tracked here until they are resolved.</p>
      )}
      <div className="ap-sec-summary">
        {SEV_ORDER.map((k) => (
          <div key={k} className={`ap-sev is-${k} ${counts[k] ? '' : 'is-zero'}`}><strong>{counts[k]}</strong><span>{k}</span></div>
        ))}
        <div className="ap-sev is-zero"><strong>{resolved}</strong><span>resolved</span></div>
      </div>
      <div className="ap-sec-meta">
        {inFlight
          ? <span><i className="ap-dot is-warn" /> <strong>{last.mode === 'active' ? 'Active' : 'Baseline'} scan running</strong>{last.stage ? ` · ${SCANNER_LABEL[last.stage] || last.stage}` : ''} · started {fmtAgo(last.createdAt)}</span>
          : lastDone
            ? <span>Last {lastDone.mode === 'active' ? 'active' : 'baseline'} scan <strong>{new Date(lastDone.finishedAt || lastDone.createdAt).toLocaleString()}</strong>{lastDone.requestedBy === 'schedule' ? ' · monthly schedule' : ''}</span>
            : <span>No scan has run yet{data.assurance ? ' — the monthly baseline is scheduled; run one now to see findings today.' : '.'}</span>}
        {data.scan?.lastScanAt && !inFlight && <span>Open findings: <strong>{scanCountLine(data.scan)}</strong></span>}
      </div>
      {(inFlight || lastDone) && (
        <div className="ap-scanners" style={{ marginTop: 8 }}>
          {(inFlight ? last : lastDone).scanners.map((sc) => {
            const st = ((inFlight ? last : lastDone).stages || []).find((x) => (x.name || x.scanner) === sc);
            return <span key={sc} className="ap-scanner" title={st?.detail || st?.status || 'pending'}><i className={`ap-dot ${stageDot(st)}`} />{SCANNER_LABEL[sc] || sc}{st?.status === 'error' ? ' · failed' : ''}</span>;
          })}
        </div>
      )}
      {isAdmin && data.assurance && (
        <div className="ap-sec-actions">
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy || inFlight} onClick={runScan} title="Passive only — reads responses, never sends attack payloads">
            {inFlight ? 'Scan in progress…' : 'Run baseline scan'}
          </button>
          {msg && <span className="cs-help" style={{ margin: 0 }}>{msg}</span>}
        </div>
      )}
      {!open.length && (lastDone || data.scan?.lastScanAt) && !inFlight && (
        <div className="ap-sec-empty">No open findings. Resolved findings stay in the ledger so a regression shows up as a re-open, not a new issue.</div>
      )}
      {open.length > 0 && (
        <div className="ap-findings">
          {SEV_ORDER.filter((k) => counts[k]).map((k) => (
            <React.Fragment key={k}>
              <div className="ap-sec-group">{k} · {counts[k]}</div>
              {open.filter((f) => f.severity === k).map((f) => (
                <div key={f.id} className={`ap-finding is-${f.severity}`}>
                  <span className="ap-finding-sev">{f.severity}</span>
                  <span className="ap-finding-name">{f.name}</span>
                  <span className="ap-scanner">{SCANNER_LABEL[f.scanner] || f.scanner}</span>
                  {f.detail ? <p className="ap-finding-detail">{f.detail}</p> : null}
                  <p className="ap-finding-where">{f.target} · first seen {new Date(f.firstSeenAt).toLocaleDateString()} · last seen {fmtAgo(f.lastSeenAt)}</p>
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </>
  );
}

// Actual AWS spend + burn rate (org admins; the endpoint 403s members and we
// hide the section rather than render an error).
export function CostsSection({ orgId, appId }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | hidden | error

  // Same 60 s cadence as Health, visibility-aware (audit P1-2): the figures
  // move as the instance reports usage, so a page left open stays current.
  useEffect(() => {
    let alive = true;
    const load = () => {
      if (document.visibilityState !== 'visible') return;
      apiGet(`/apps/${orgId}/${appId}/costs`)
        .then((d) => { if (alive) { setData(d); setStatus('ok'); } })
        .catch((e) => { if (alive) setStatus(e.status === 403 ? 'hidden' : 'error'); });
    };
    load();
    const t = setInterval(load, 60000);
    document.addEventListener('visibilitychange', load);
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', load); };
  }, [orgId, appId]);

  if (status === 'hidden') return null;
  if (status === 'loading') return <section className="ap-section"><h2>Costs</h2><p className="cs-help">Loading costs…</p></section>;
  if (status === 'error') return <section className="ap-section"><h2>Costs</h2><p className="cs-help">Cost data unavailable.</p></section>;

  const c = data.current;
  const fmt = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
  const services = Object.entries(c?.infra?.byService || c?.byService || {}).sort((a, b) => b[1] - a[1]);
  const models = Object.entries(c?.model?.byModel || {}).sort((a, b) => b[1].usd - a[1].usd);
  const users = Object.entries(c?.byUser || {}).sort((a, b) => b[1].usd - a[1].usd).slice(0, 8);
  const badge = (basis) => <span className={`ap-badge ${basis === 'actual' ? 'is-ok' : 'is-muted'}`} style={{ marginLeft: 6, fontSize: 10 }}>{basis}</span>;

  return (
    <section className="ap-section">
      <h2>Costs · {data.period}</h2>
      {!c ? (
        <p className="cs-help">
          No cost data yet — AWS actuals appear ~24h after the app&apos;s
          resources are tagged for cost allocation.
        </p>
      ) : (
        <>
          <div className="ap-health-stats">
            <div className="ap-health-stat"><strong>{fmt(c.mtdUsd)}</strong><span className="cs-help">month to date</span></div>
            <div className="ap-health-stat"><strong>{fmt(c.burnPerDayUsd)}</strong><span className="cs-help">burn / day</span></div>
            <div className="ap-health-stat"><strong>{fmt(c.projectedUsd)}</strong><span className="cs-help">projected</span></div>
            {data.prior && (
              <div className="ap-health-stat"><strong>{fmt(data.prior.mtdUsd)}</strong><span className="cs-help">{data.priorPeriod} total</span></div>
            )}
          </div>
          <div className="ap-cost-services">
            <div className="ap-cost-service">
              <span className="ap-cost-svc-name" style={{ color: 'var(--text)' }}>Infrastructure{badge('actual')}</span>
              <span className="ap-cost-svc-usd">{fmt(c.infra?.usd ?? c.mtdUsd)}</span>
            </div>
            {services.map(([svc, usd]) => (
              <div key={svc} className="ap-cost-service" style={{ paddingLeft: 18 }}>
                <span className="ap-cost-svc-name">{svc}</span>
                <span className="ap-cost-svc-usd">{fmt(usd)}</span>
              </div>
            ))}
            {c.hostShareUsd > 0 && (
              <div className="ap-cost-service" style={{ paddingLeft: 18 }}>
                <span className="ap-cost-svc-name">Shared host apportionment</span>
                <span className="ap-cost-svc-usd">{fmt(c.hostShareUsd)}</span>
              </div>
            )}
            {c.model && (
              <div className="ap-cost-service">
                <span className="ap-cost-svc-name" style={{ color: 'var(--text)' }}>Model usage{badge('estimated')}</span>
                <span className="ap-cost-svc-usd">{fmt(c.model.usd)}</span>
              </div>
            )}
            {models.map(([m, row]) => (
              <div key={m} className="ap-cost-service" style={{ paddingLeft: 18 }}>
                <span className="ap-cost-svc-name">{m.replace('__', ' / ')}</span>
                <span className="ap-cost-svc-usd">{fmt(row.usd)}</span>
              </div>
            ))}
            {c.dev && c.dev.usd > 0 && (
              <div className="ap-cost-service">
                <span className="ap-cost-svc-name" style={{ color: 'var(--text)' }}>Dev Console agents{badge('actual')}</span>
                <span className="ap-cost-svc-usd">{fmt(c.dev.usd)}</span>
              </div>
            )}
          </div>
          {users.length > 0 && (
            <>
              <h3 style={{ margin: '14px 0 6px', fontSize: 13 }}>Top users (model spend, estimated)</h3>
              <div className="ap-cost-services" style={{ marginTop: 0 }}>
                {users.map(([uid, u]) => (
                  <div key={uid} className="ap-cost-service">
                    <span className="ap-cost-svc-name">{uid === 'other' ? 'Everyone else' : uid}</span>
                    <span className="ap-cost-svc-usd">{fmt(u.usd)} <span className="cs-help" style={{ margin: 0, display: 'inline' }}>· {(u.tokens || 0).toLocaleString()} tok · {u.aiTurns || 0} turns</span></span>
                  </div>
                ))}
              </div>
            </>
          )}
          {c.reportedAt && <p className="cs-help" style={{ marginTop: 8 }}>Usage last reported {new Date(c.reportedAt).toLocaleString()}{c.infra?.pulledAt ? ` · AWS actuals pulled ${new Date(c.infra.pulledAt).toLocaleString()}` : ''}</p>}
        </>
      )}
    </section>
  );
}

// UAT add-on: a full second stamped instance (<slug>-uat, own stack/DB, same
// layer repo). Once live, the prod Dev Console gains the UAT push target and
// the promote button (the provisioner wires the pair automatically).
// Environments: prod beside its UAT twin, the last deploy on each, Deploy to
// UAT (any layer ref) and Promote to prod (main, type-to-confirm). Deploys go
// through the control plane (POST /apps/:org/:app/deploy → the instance's
// /dev/remote-deploy) and are mirrored into apps/{id}/deploys (appDeploy).
const shortSha = (s) => (s ? String(s).slice(0, 8) : '');
const agoText = (ts) => {
  if (!ts) return '';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 172800) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

// Provisioning errors arrive as raw stage output. Lead with the plain fact
// (which stage failed) and keep the raw text behind a disclosure.
function humanizeStatusDetail(detail, job) {
  const failed = (job?.stages || []).find((st) => STAGE_ERR.includes(String(st.status || '').toLowerCase()));
  const text = String(detail || '');
  const short = text.length > 160 || /\n/.test(text);
  return (
    <span>
      <strong>Provisioning failed{failed ? ` at “${failed.name}”` : ''}.</strong>{' '}
      {short ? (
        <details style={{ display: 'inline-block', marginTop: 4 }}>
          <summary className="cs-help" style={{ margin: 0, cursor: 'pointer' }}>Show details</summary>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, marginTop: 6 }}>{text}</pre>
        </details>
      ) : text}
    </span>
  );
}

function EnvColumn({ label, inst, lastDeploy, children }) {
  return (
    <div className={`ap-env ${label === 'Production' ? 'is-prod' : ''}`}>
      <div className="ap-env-head">
        <strong>{label}</strong>
        {inst ? <StatusBadge status={inst.status} /> : <span className="ap-badge is-draft">none</span>}
        {inst?.monitor?.state ? <span className={`ap-badge ${inst.monitor.state === 'up' ? 'is-ok' : inst.monitor.state === 'down' ? 'is-err' : 'is-draft'}`}>{inst.monitor.state}</span> : null}
      </div>
      {inst && appUrl(inst) ? (
        <a className="ap-card-url" href={appUrl(inst)} target="_blank" rel="noreferrer">{appUrl(inst).replace(/^https?:\/\//, '')}</a>
      ) : null}
      <p className="cs-help" style={{ margin: '4px 0' }}>
        Kit {inst?.version ? <code>{shortSha(inst.version)}</code> : (inst?.repo?.kitRef || inst?.kitRef || '—')}
        {inst?.monitor?.tlsDaysLeft != null ? ` · TLS ${inst.monitor.tlsDaysLeft}d` : ''}
        {inst?.monitor?.lastLatencyMs != null ? ` · ${inst.monitor.lastLatencyMs}ms` : ''}
      </p>
      <p className="cs-help" style={{ margin: '4px 0' }}>
        {lastDeploy ? (
          <>
            Last deploy {agoText(lastDeploy.finishedAt || lastDeploy.updatedAt)} · {lastDeploy.ref || 'main'}
            {lastDeploy.headSha ? <> · <code>{shortSha(lastDeploy.headSha)}</code></> : null}
            {' · '}{lastDeploy.status}
          </>
        ) : (label === 'Production' ? 'No promotes yet — changes reach production only through “Promote”.' : 'No deploys yet — push from the Dev Console or “Deploy to UAT”.')}
      </p>
      {children}
    </div>
  );
}

export function EnvironmentsPanel({ org, app, uatDoc, isAdmin, compact = false }) {
  const { docs: deploys } = useEntityList('appDeploy', { org: org.id, appInstance: app?.id }, { enabled: !!app?.id });
  const [ref, setRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [word, setWord] = useState('');

  const last = (env) => (deploys || []).find((d) => d.env === env) || null;
  const running = (deploys || []).some((d) => d.status === 'running');
  const lastUat = last('uat');
  const lastProd = last('prod');

  const deploy = async (env) => {
    setBusy(true);
    setMsg('');
    try {
      const out = await apiPost(`/apps/${org.id}/${app.id}/deploy`, { env, ref: env === 'uat' ? ref.trim() : '' });
      setMsg(`${env === 'uat' ? 'UAT deploy' : 'Promote to production'} started (${out.targets.join(', ')}${out.ref ? ` @ ${out.ref}` : ''}).`);
      setConfirming(false);
      setWord('');
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!uatDoc || uatDoc.status === 'terminated') return null;
  // Site mode: the prod stack is off (and the twin suspended) — no pushes.
  const parked = app?.mode === 'site';
  const canPromote = isAdmin && !running && !parked && lastUat?.status === 'done';

  return (
    <div className="ap-envs">
      <div className={`ap-env-grid ${compact ? 'is-compact' : ''}`}>
        <EnvColumn label="UAT" inst={uatDoc} lastDeploy={lastUat}>
          {isAdmin && !compact ? (
            <div className="ap-env-actions">
              <input className="cs-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="main (layer branch or tag)" disabled={busy} aria-label="ref to deploy to UAT" />
              <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy || running || parked} title={parked ? SITE_MODE_NOTE : undefined} onClick={() => deploy('uat')}>
                Deploy to UAT
              </button>
            </div>
          ) : null}
        </EnvColumn>
        <EnvColumn label="Production" inst={app} lastDeploy={lastProd}>
          {isAdmin && !compact ? (
            confirming ? (
              <div className="ap-env-actions" data-agent-guard>
                <span className="cs-help" style={{ margin: 0 }}>Type <strong>promote</strong> to deploy main to production:</span>
                <input className="cs-input" value={word} onChange={(e) => setWord(e.target.value)} placeholder="promote" />
                <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy || word !== 'promote'} onClick={() => deploy('prod')}>Promote</button>
                <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => { setConfirming(false); setWord(''); }}>Cancel</button>
              </div>
            ) : (
              <div className="ap-env-actions">
                <button
                  type="button"
                  className="cs-btn cs-btn-sm cs-btn-outline"
                  disabled={!canPromote || busy}
                  title={canPromote ? 'Deploy main to production' : parked ? SITE_MODE_NOTE : running ? 'a deploy is running' : 'deploy to UAT and verify it first'}
                  onClick={() => setConfirming(true)}
                >
                  Promote to production
                </button>
              </div>
            )
          ) : null}
        </EnvColumn>
      </div>
      {msg ? <p className="cs-help">{msg}</p> : null}
      {!compact && deploys?.length ? (
        <ul className="ap-deploys">
          {deploys.slice(0, 8).map((d) => (
            <li key={d.id} className={`ap-deploy is-${d.status}`}>
              <span className={`ap-badge ${d.env === 'prod' ? 'is-ok' : 'is-info'}`}>{d.env}</span>
              <span>{d.ref || 'main'}{d.headSha ? <> · <code>{shortSha(d.headSha)}</code></> : null}</span>
              <span className="cs-help" style={{ margin: 0 }}>{(d.targets || []).join(', ')}</span>
              <span className={`ap-badge ${d.status === 'done' ? 'is-ok' : d.status === 'error' ? 'is-err' : 'is-info'}`}>{d.status === 'running' ? (d.stage || 'running') : d.status}</span>
              <span className="cs-help" style={{ margin: 0 }}>{agoText(d.updatedAt)}{d.requestedBy ? ` · ${d.requestedBy}` : ''}</span>
              {d.message ? (
                d.status === 'error' ? (
                  <details className="ap-deploy-err">
                    <summary>Deploy failed{d.stage ? ` at ${d.stage}` : ''} — details</summary>
                    <pre>{d.message}</pre>
                  </details>
                ) : <span className="cs-help" style={{ margin: 0 }}>{d.message}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function UatSection({ org, appId, app, isAdmin, canEdit = isAdmin }) {
  const uatDoc = useOrgDoc('appInstance', 'apps', org.id, app?.uatAppId || null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [payDocs, setPayDocs] = useState(null);

  const addUat = async () => {
    setBusy(true);
    setErr('');
    try {
      const out = await apiPost(`/apps/${org.id}/${appId}/uat`);
      if (out.provisioned) { setBusy(false); return; } // internal org — live doc updates
      window.location.href = out.url;
    } catch (e) {
      if (e.status === 409) {
        try { setPayDocs(await fetchAgreements()); setErr(''); }
        catch (ex) { setErr(ex.message); }
      } else setErr(e.message);
      setBusy(false);
    }
  };

  if (app?.uatAppId && uatDoc && uatDoc.status !== 'terminated') {
    return (
      <div>
        <p className="cs-help" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <StatusBadge status={uatDoc.status} />
          <span>{appHost(uatDoc)}</span>
          {uatDoc.status === 'live' && appUrl(uatDoc) && (
            <a className="cs-btn cs-btn-outline cs-btn-sm" href={appUrl(uatDoc)} target="_blank" rel="noreferrer">Open UAT</a>
          )}
          <Link className="cs-btn cs-btn-outline cs-btn-sm" to={`/orgs/${org.id}/apps/${app.uatAppId}`}>Details</Link>
        </p>
        <p className="cs-help">
          Pushes from the production Dev Console land here first; promote to production when checks pass.
        </p>
        <EnvironmentsPanel org={org} app={{ ...app, id: appId }} uatDoc={uatDoc} isAdmin={isAdmin} />
      </div>
    );
  }
  return (
    <div>
      <p className="cs-help">
        A full second instance of your app (<code>{app?.slug}-uat</code> URL, its own database)
        to test changes on before they touch production. The Dev Console pushes to UAT first
        and promotes deliberately. Billed as a monthly add-on.
      </p>
      {payDocs && <OrgAgree orgId={org.id} docs={payDocs} onDone={addUat} />}
      {canEdit ? (
        <button className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy} onClick={addUat}>
          {busy ? 'Redirecting…' : 'Add a UAT environment'}
        </button>
      ) : (
        <p className="cs-help">A member of this organization can add this.</p>
      )}
      {err && <p className="cs-help ap-slug-bad">{err}</p>}
    </div>
  );
}

// One-click Dev Console: POST /apps/:org/:app/sso returns a 90-second
// signed URL on the instance that signs the user in with their nventr.io
// identity and lands on /dev. The tab is opened synchronously (popup
// blockers) and pointed at the URL when it arrives; any refusal (older kit,
// no permission) falls back to the plain Dev Console link.
// Site mode (appInstance.mode === 'site'): the instance's stack is stopped
// and only its static public site is served — nothing that needs the
// instance backend can work, so the buttons say so instead of failing.
export const SITE_MODE_NOTE = 'In site mode: only the public site is served. Sign-in, the app, Dev Console, pushes and the assistant are off until an operator switches the instance back to app mode.';

export function DevConsoleButton({ orgId, appId, devUrl, className = 'cs-btn cs-btn-outline cs-btn-sm', label = 'Open Dev Console', next, siteMode = false }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  if (!devUrl) return null;
  if (siteMode) return <button type="button" className={className} disabled title={SITE_MODE_NOTE}>{label}</button>;
  const open = async () => {
    setBusy(true); setNote('');
    const w = window.open('about:blank', '_blank');
    try {
      const { url } = await apiPost(`/apps/${orgId}/${appId}/sso`, next ? { next } : {});
      if (w) w.location = url; else window.location.assign(url);
    } catch (e) {
      if (w) w.location = devUrl; else window.open(devUrl, '_blank');
      setNote(e.message);
    } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" className={className} disabled={busy} onClick={open} title="Signs you in with your nventr.io account">
        {busy ? 'Signing in…' : label}
      </button>
      {note && <span className="cs-help" style={{ margin: '0 0 0 8px' }}>Opened without single sign-on: {note}</span>}
    </>
  );
}

// Dev Console access: the instance runs its own sign-in (GoTrue on the
// instance), so an nventr.io session does not open /dev by itself. The
// "Open Dev Console" button signs in through the control plane (above); the
// creator and org admins can also mint or rotate an instance password here
// for signing in directly, and admins can grant another member. The
// password is shown exactly once.
export function DevAccessSection({ orgId, appId, app }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [issued, setIssued] = useState(null); // { email, password, rotated }
  const [grantEmail, setGrantEmail] = useState('');
  const [msg, setMsg] = useState('');
  const [copied, setCopied] = useState('');

  const load = () => apiGet(`/apps/${orgId}/${appId}/dev-access`)
    .then((d) => { setData(d); setErr(''); })
    .catch((e) => setErr(e.message));
  useEffect(() => { load(); }, [orgId, appId, app?.devAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const mint = async (email) => {
    setBusy(email || 'me'); setMsg(''); setIssued(null);
    try {
      const out = await apiPost(`/apps/${orgId}/${appId}/dev-access`, email ? { email } : {});
      setIssued(out);
      setGrantEmail('');
      await load();
    } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  };
  const copy = async (label, text) => {
    try { await navigator.clipboard.writeText(text); setCopied(label); setTimeout(() => setCopied(''), 1500); } catch { /* clipboard blocked */ }
  };

  if (err) return <p className="cs-help">Dev Console access unavailable: {err}</p>;
  if (!data) return <p className="cs-help">Loading…</p>;

  const devUrl = data.devUrl || app?.urls?.dev;
  const mine = data.me?.entry;
  const others = (data.entries || []).filter((e) => e.email !== data.me?.email);
  const grantable = (data.members || []).filter((m) => m.email !== data.me?.email && !(data.entries || []).some((e) => e.email === m.email));

  return (
    <div className="ap-devaccess">
      <div className="ap-banner is-info">
        <strong>One click, signed in.</strong> <code>{hostOf(devUrl)}</code> runs its own auth system, but <em>Open Dev Console</em>
        signs you in there with your nventr.io account (app creator, org admins and members listed below). Mint an instance
        password only if you want to sign in directly at <code>{hostOf(devUrl)}/login</code>.
        {(data.canMint || mine) && <> <DevConsoleButton orgId={orgId} appId={appId} devUrl={devUrl} className="cs-btn cs-btn-primary cs-btn-sm" /></>}
      </div>

      {issued && (
        <div className="ap-devaccess-issued">
          <div className="ap-devaccess-issued-head">
            <i className="ap-dot is-ok" />
            <strong>{issued.rotated ? 'Password rotated' : 'Operator account ready'} for {issued.email}</strong>
            <span className="cs-help" style={{ margin: 0 }}>Shown once — copy it now. If it is lost, rotate here or use “Forgot password?” on the instance.</span>
          </div>
          <dl className="ap-devaccess-cred">
            <dt>Sign in at</dt><dd><a href={issued.loginUrl || devUrl} target="_blank" rel="noreferrer">{issued.loginUrl || devUrl}</a></dd>
            <dt>Email</dt><dd><code>{issued.email}</code> <button type="button" className="ap-lnk" onClick={() => copy('email', issued.email)}>{copied === 'email' ? 'copied' : 'copy'}</button></dd>
            <dt>Password</dt><dd><code className="ap-devaccess-pw">{issued.password}</code> <button type="button" className="ap-lnk" onClick={() => copy('pw', issued.password)}>{copied === 'pw' ? 'copied' : 'copy'}</button></dd>
          </dl>
          <a className="cs-btn cs-btn-primary cs-btn-sm" href={devUrl} target="_blank" rel="noreferrer">Open Dev Console</a>
        </div>
      )}

      <table className="ap-checks ap-devaccess-table">
        <thead><tr><th>Operator</th><th>Status</th><th>Since</th><th /></tr></thead>
        <tbody>
          <tr>
            <td><code>{data.me?.email}</code> <span className="ap-strip-sub">you</span></td>
            <td>{mine ? <span><i className="ap-dot is-ok" /> {mine.ssoAt ? 'signs in via nventr.io' : 'access minted'}{mine.rotations ? ` · rotated ×${mine.rotations}` : ''}</span> : <span><i className="ap-dot" /> {data.canMint ? 'not signed in yet — use Open Dev Console' : 'no instance account yet'}</span>}</td>
            <td className="cs-time">{mine ? fmtAgo(mine.updatedAt || mine.mintedAt) : '—'}</td>
            <td>
              {data.canMint ? (
                <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={!!busy || !data.available} onClick={() => mint()} title={data.available ? '' : 'Instance must be live on the managed fleet'}>
                  {busy === 'me' ? 'Minting…' : mine ? 'Rotate password' : 'Mint my access'}
                </button>
              ) : <span className="cs-help" style={{ margin: 0 }}>ask the app creator or an org admin</span>}
            </td>
          </tr>
          {others.map((e) => (
            <tr key={e.uid || e.email}>
              <td><code>{e.email}</code></td>
              <td><span><i className="ap-dot is-ok" /> {e.ssoAt ? 'signs in via nventr.io' : 'access minted'}{e.rotations ? ` · rotated ×${e.rotations}` : ''}</span></td>
              <td className="cs-time">{fmtAgo(e.updatedAt || e.mintedAt)}<span className="ap-strip-sub"> · by {e.updatedBy || e.mintedBy}</span></td>
              <td>
                {data.canGrant && (
                  <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={!!busy || !data.available} onClick={() => mint(e.email)}>
                    {busy === e.email ? 'Rotating…' : 'Rotate password'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {data.canGrant && data.available && (
        <form className="ap-check-form ap-devaccess-grant" onSubmit={(e) => { e.preventDefault(); if (grantEmail) mint(grantEmail); }}>
          <select value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)}>
            <option value="">Grant another team member…</option>
            {grantable.map((m) => <option key={m.uid} value={m.email}>{m.email} · {m.role}</option>)}
          </select>
          <button type="submit" className="cs-btn cs-btn-primary cs-btn-sm" disabled={!grantEmail || !!busy}>{busy && busy === grantEmail ? 'Minting…' : 'Mint access'}</button>
          <span className="cs-help" style={{ margin: 0 }}>Members only — invite people to the organization first. You will see their password once; pass it on securely.</span>
        </form>
      )}
      {msg && <p className="cs-help ap-slug-bad">{msg}</p>}
    </div>
  );
}
const hostOf = (u) => { try { return new URL(u).host; } catch { return u || 'your instance'; } };

// Same scene the kit's Dev Console shows, fed by the control plane's view of
// this instance (GET /apps/:orgId/:appId/ecosystem). Polls every 30s while
// the tab is visible; the feed's `activity` doubles as the ticker.
export function EcosystemSection({ orgId, appId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const out = await apiGet(`/apps/${orgId}/${appId}/ecosystem`);
        if (alive) { setData(out); setErr(''); }
      } catch (e) {
        if (alive) setErr(e.message);
      }
    };
    load();
    const t = setInterval(load, 30000);
    document.addEventListener('visibilitychange', load);
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', load); };
  }, [orgId, appId]);
  return (
    <>
      <EcosystemFrame data={data} />
      {err && <p className="cs-help">Ecosystem feed: {err}</p>}
      {/* The scene is the picture; the list — what each service is, how it is doing — is the Services page (audit P1-4). */}
      <p className="cs-help" style={{ marginTop: 6 }}><Link to={`/orgs/${orgId}/apps/${appId}/live/services`}>Services as a list</Link> — what each part is, its state, restarts and memory, as the instance reports it.</p>
    </>
  );
}

// Keys from the Vault — which Administration → Vault entries are bound to
// this instance, with placement state. Binding happens in the Vault (deep
// link carries this appId); here is where someone looking at the app asks
// "which keys does it have?". Names and fingerprints only.
export function VaultKeysSection({ orgId, appId, app }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!isBackendConfigured()) return;
    apiGet(`/orgs/${orgId}/vault`).then((d) => { setData(d); setErr(''); }).catch((e) => setErr(e.message));
  }, [orgId, appId]);
  const vaultTo = `/orgs/${orgId}/administration?tab=vault&bind=${encodeURIComponent(appId)}`;
  if (err) return <p className="cs-help">Vault unavailable: {err}</p>;
  if (!data) return <p className="cs-help">Loading…</p>;
  const mine = (data.entries || []).map((e) => ({ e, b: (e.bindings || []).find((x) => x.appId === appId) })).filter((x) => x.b);
  return (
    <div className="ap-vaultkeys">
      {mine.length ? (
        <table className="mf-table">
          <thead><tr><th>Key on the instance</th><th>Vault entry</th><th>Where</th><th>State</th></tr></thead>
          <tbody>
            {mine.map(({ e, b }) => (
              <tr key={e.id}>
                <td><code>secrets/{b.as || e.name}</code></td>
                <td>{e.name} <span className="mf-muted">· {e.kind} · v{e.version || 1}</span></td>
                <td className="mf-muted">{(b.envs || ['prod']).join(' + ')}</td>
                <td>{b.error ? <span title={b.error}><i className="ap-dot is-warn" /> not placed</span> : b.syncedVersion === e.version ? <span><i className="ap-dot is-ok" /> placed</span> : <span><i className="ap-dot" /> stale — re-place from the Vault</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="cs-help">No Vault keys bound to this instance. Keys stored in the Vault are placed here as file secrets and placed again after every re-stamp.</p>
      )}
      <p className="cs-help" style={{ marginTop: 8 }}>
        <Link to={vaultTo}>Bind a key from the Vault →</Link>
        {app?.urls?.dev && <> · one-off keys can also be set in the instance&rsquo;s Dev Console → Secrets.</>}
      </p>
    </div>
  );
}

// Collapsible app-detail section. Open/closed state is remembered per
// section id (localStorage) so a customer's preferred layout sticks; the
// anchor id makes the summary strip's jump links land here.
const SECTION_KEY = 'nv.ap.sections';
function readSections() {
  try { return JSON.parse(localStorage.getItem(SECTION_KEY) || '{}'); } catch { return {}; }
}
export function ApSection({ id, title, defaultOpen = true, meta = null, className = '', children }) {
  const [open, setOpen] = useState(() => {
    const saved = readSections()[id];
    return typeof saved === 'boolean' ? saved : defaultOpen;
  });
  const toggle = (e) => {
    e.preventDefault();
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(SECTION_KEY, JSON.stringify({ ...readSections(), [id]: next })); } catch { /* ignore */ }
  };
  return (
    <details className={`ap-section ap-fold ${className}`} id={id} open={open}>
      <summary onClick={toggle}>
        <h2>{title}</h2>
        {meta && <span className="cs-help ap-fold-meta">{meta}</span>}
        <span className="ap-fold-chev" aria-hidden="true">{open ? '−' : '+'}</span>
      </summary>
      {open && <div className="ap-fold-body">{children}</div>}
    </details>
  );
}

// One line for the three repositories, plus the admin form that designates
// the git a reviewed batch is pushed to. The instance repo stays the deploy
// source. A push branch equal to the protected branch is refused by the API.
function reposLineOf(app) {
  const repo = app?.repo || {};
  const kitRef = repo.kitRef || app?.monitor?.kitRef || '';
  // Hosted as it is: the mirror app-<slug> is the instance repo the Dev
  // Console works; layer-<slug> is only the console's own shell.
  const keep = app?.adoption?.intent === 'keep' && app?.appRepo?.name;
  const instanceRepo = keep ? app.appRepo.name : (repo.name || '');
  const remote = app?.pushRemote || null;
  const kit = keep ? `App Kit${kitRef ? ` ${kitRef}` : ''} (console only)` : (kitRef ? `App Kit ${kitRef}` : 'App Kit');
  const inst = instanceRepo ? `instance repo ${instanceRepo}${keep ? ' (your app, mirrored whole)' : ''}` : 'instance repo';
  let third = 'their git is not designated';
  if (remote?.slug) {
    const where = remote.branch ? `${remote.slug} @ ${remote.branch}` : `${remote.slug} (branch not designated)`;
    const guard = remote.protectedBranch ? `; ${remote.protectedBranch} stays protected` : '';
    third = `pushed to ${where}${guard}`;
  }
  return `${kit} · ${inst} · ${third}`;
}

function ReposLine({ org, app, isAdmin }) {
  const remote = app?.pushRemote || null;
  const [provider, setProvider] = useState(remote?.provider || 'github');
  const [slug, setSlug] = useState(remote?.slug || '');
  const [branch, setBranch] = useState(remote?.branch || '');
  const [protectedBranch, setProtectedBranch] = useState(remote?.protectedBranch || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    setProvider(remote?.provider || 'github');
    setSlug(remote?.slug || '');
    setBranch(remote?.branch || '');
    setProtectedBranch(remote?.protectedBranch || '');
  }, [remote?.provider, remote?.slug, remote?.branch, remote?.protectedBranch]);
  const save = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await apiPost(`/apps/${org.id}/${app.id}/push-remote`, {
        provider, slug: slug.trim(), branch: branch.trim(), protectedBranch: protectedBranch.trim(),
      });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} data-nova-note="The App Kit, this instance's repository, and the git a reviewed batch is pushed to. The branch that still deploys the current site is protected." style={{ margin: '8px 0 0' }}>
      <p className="cs-help" style={{ margin: '0 0 6px' }}>{reposLineOf(app)}</p>
      {isAdmin && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="cs-input" style={{ width: 120 }} value={provider} onChange={(e) => setProvider(e.target.value)} aria-label="Git provider">
            <option value="github">GitHub</option>
            <option value="gitea">Gitea</option>
            <option value="codecommit">CodeCommit</option>
          </select>
          <input className="cs-input" style={{ width: 220 }} value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="owner/name" aria-label="Repository to push to" />
          <input className="cs-input" style={{ width: 140 }} value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="branch to push" aria-label="Push branch" />
          <input className="cs-input" style={{ width: 140 }} value={protectedBranch} onChange={(e) => setProtectedBranch(e.target.value)} placeholder="protected branch" aria-label="Protected branch" />
          <button type="submit" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy} data-agent-guard>{busy ? 'Saving…' : 'Save'}</button>
          {remote?.slug && (
            <button
              type="button"
              className="cs-btn cs-btn-sm cs-btn-ghost"
              disabled={busy}
              data-agent-guard
              onClick={async () => {
                setBusy(true);
                setErr('');
                try { await apiPost(`/apps/${org.id}/${app.id}/push-remote`, { clear: true }); }
                catch (e) { setErr(e.message); }
                finally { setBusy(false); }
              }}
            >
              Clear
            </button>
          )}
          {err && <span className="ap-slug-bad">{err}</span>}
        </div>
      )}
    </form>
  );
}

// One-glance ops strip for a live app: what is it, where is it, is it
// healthy, when did it last change — with jump links to the detail below.
function SummaryStrip({ org, app, uatDoc, isAdmin }) {
  const mon = app?.monitor || null;
  const dot = mon?.state === 'up' ? 'is-ok' : mon?.state === 'down' ? 'is-down' : mon?.state ? 'is-warn' : 'is-off';
  const twinOk = uatDoc && uatDoc.status !== 'terminated';
  const counts = app?.scan?.counts || {};
  const openSev = SEV_ORDER.filter((k) => counts[k]);
  const kitLabel = app?.version ? shortSha(app.version) : (app?.repo?.kitRef || app?.kitRef || '—');
  const jump = (id) => (e) => { e.preventDefault(); const el = document.getElementById(id); if (el) { el.open = true; el.scrollIntoView({ behavior: 'smooth', block: 'start' }); } };
  return (
    <div className="ap-strip" role="region" aria-label="App at a glance">
      <div className="ap-strip-cells">
        <div className="ap-strip-cell">
          <span className="ap-strip-k">Health</span>
          <span className="ap-strip-v"><i className={`ap-dot ${dot}`} />{mon?.state || 'no data'}{mon?.lastLatencyMs != null ? ` · ${mon.lastLatencyMs}ms` : ''}</span>
          {mon?.lastCheckAt ? <span className="ap-strip-sub">checked {fmtAgo(mon.lastCheckAt)}</span> : null}
        </div>
        <div className="ap-strip-cell is-wide">
          <span className="ap-strip-k">Production</span>
          <span className="ap-strip-v">{appUrl(app) ? <a href={appUrl(app)} target="_blank" rel="noreferrer">{appHost(app)}</a> : '—'}</span>
          {app?.region ? <span className="ap-strip-sub">{app.region}{app?.host ? ` · host ${String(app.host).slice(-6)}` : ''}</span> : null}
        </div>
        <div className="ap-strip-cell is-wide">
          <span className="ap-strip-k">UAT twin</span>
          <span className="ap-strip-v">
            {app?.adoption?.intent === 'keep'
              ? (app.urls?.appRuntimeUat
                ? <a href={app.urls.appRuntimeUat} target="_blank" rel="noreferrer">{app.urls.appRuntimeUat.replace(/^https?:\/\//, '')}</a>
                : <span className="cs-help" style={{ margin: 0 }}>on your pipeline</span>)
              : twinOk
                ? (appUrl(uatDoc) ? <a href={appUrl(uatDoc)} target="_blank" rel="noreferrer">{appHost(uatDoc)}</a> : uatDoc.status)
                : isAdmin && (app?.envRole || 'prod') === 'prod'
                  ? <a href="#ap-envs" className="ap-lnk" onClick={jump('ap-envs')}>Add a UAT environment</a>
                  : <span className="cs-help" style={{ margin: 0 }}>none</span>}
          </span>
          {twinOk && uatDoc?.monitor?.state ? <span className="ap-strip-sub"><i className={`ap-dot ${uatDoc.monitor.state === 'up' ? 'is-ok' : 'is-down'}`} /> {uatDoc.monitor.state}</span> : null}
        </div>
        <div className="ap-strip-cell">
          <span className="ap-strip-k">Security</span>
          <span className="ap-strip-v">
            {!app?.assurance
              ? <span className="cs-help" style={{ margin: 0 }}>not enabled</span>
              : !app?.scan?.lastScanAt
                ? <a href="#ap-security" className="ap-lnk" onClick={jump('ap-security')}>scan pending</a>
                : openSev.length
                  ? openSev.map((k) => <span key={k} className={`ap-sevchip is-${k}`}>{counts[k]} {k}</span>)
                  : <span><i className="ap-dot is-ok" /> no open findings</span>}
          </span>
          {app?.scan?.lastScanAt ? <span className="ap-strip-sub">scanned {fmtAgo(app.scan.lastScanAt)}</span> : null}
        </div>
        <div className="ap-strip-cell">
          <span className="ap-strip-k">Kit</span>
          <span className="ap-strip-v">{kitLabel}{mon?.versionBehind ? <span className="ap-strip-warn"> · update available</span> : ''}{mon?.runtimeBehind ? <span className="ap-strip-warn" title={`Node ${mon.node} is below the patched release the plane requires; the next kit update rebuilds the image`}> · runtime behind</span> : ''}</span>
          {app?.template?.appType ? <span className="ap-strip-sub">{app.template.appType} template{app?.assurance ? ' · assurance' : ''}{app?.template?.addons?.voice ? ' · voice' : ''}{mon?.node ? ` · Node ${mon.node}` : ''}</span> : null}
        </div>
        {mon?.tlsDaysLeft != null && (
          <div className="ap-strip-cell">
            <span className="ap-strip-k">TLS</span>
            <span className="ap-strip-v"><i className={`ap-dot ${mon.tlsDaysLeft < 7 ? 'is-down' : mon.tlsDaysLeft < 14 ? 'is-warn' : 'is-ok'}`} />{mon.tlsDaysLeft}d left</span>
            <span className="ap-strip-sub">auto-renews</span>
          </div>
        )}
      </div>
      <ReposLine org={org} app={app} isAdmin={isAdmin} />
      <nav className="ap-strip-jump" aria-label="Sections">
        {[['ap-health', 'Health'], ['ap-security', 'Security'], ['ap-devaccess', 'Dev Console'], ['ap-vault', 'Vault keys'], ['ap-envs', 'Environments'], ['ap-mail', 'Email'], ['ap-eco', 'Live picture'], ['ap-costs', 'Costs'], ['ap-usage', 'Usage'], ['ap-invoices', 'Invoices'], ['ap-team', 'Team']].map(([id, label]) => (
          <a key={id} href={`#${id}`} onClick={jump(id)}>{label}</a>
        ))}
      </nav>
    </div>
  );
}

// Which workspace the app (and its twin) live in; org admins can move it.
// Moving changes who can see the app — members without a seat in the target
// workspace lose it from their lists immediately.
function WorkspaceLine({ org, app, appId, isOrgAdmin }) {
  const tenant = useTenant();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const current = (tenant.workspaces || []).find((w) => w.id === app.workspaceId);
  const move = async (wsId) => {
    setBusy(true); setErr('');
    try { await apiPost(`/apps/${org.id}/${appId}/workspace`, { workspaceId: wsId || null }); } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <p className="cs-help" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <span>Workspace: <strong style={{ color: 'var(--text)' }}>{app.workspaceId ? (current?.name || app.workspaceId) : 'org-wide'}</strong></span>
      {isOrgAdmin && (
        <select className="cs-input" style={{ width: 200, padding: '2px 6px', fontSize: 12 }} value={app.workspaceId || ''} disabled={busy} onChange={(e) => move(e.target.value)} aria-label="Move to workspace">
          <option value="">org-wide (every member)</option>
          {(tenant.workspaces || []).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      )}
      {err && <span className="ap-slug-bad">{err}</span>}
    </p>
  );
}

// How an app hosted as it is runs (adoption.runtime) and whether it answers
// (adoption.runtimeStatus, monitor.runtime). Admins switch between nventr
// running the container and their own pipeline, or fix the port / health
// path; a save re-runs the app-runtime stage on the host.
function RuntimeLine({ org, appId, app, isAdmin }) {
  const rt = app?.adoption?.runtime || { kind: 'downstream' };
  const status = app?.adoption?.runtimeStatus || null;
  const probe = app?.monitor?.runtime || null;
  const [form, setForm] = useState(null); // null = not editing
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await apiPost(`/apps/${org.id}/${appId}/runtime`, { runtime: form.kind === 'container' ? { kind: 'container', dockerfile: form.dockerfile, port: Number(form.port), healthPath: form.healthPath, ...(form.context.trim() ? { context: form.context.trim() } : {}), buildArgs: form.buildArgs, memory: form.memory } : { kind: 'downstream' } });
      setForm(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-nova-note="How the brought app runs: nventr builds its Dockerfile into a container beside the console (its own URL, UAT twin container, app-env secret), or the customer's own pipeline deploys it. Saving re-runs the build on the host." style={{ margin: '0 0 8px' }}>
      <p className="cs-help" style={{ margin: '0 0 4px' }}>
        {RUNTIME_COPY[rt.kind] || rt.kind}
        {rt.kind === 'container' && app.urls?.appRuntime && (
          <>
            {' '}Your app: <a href={app.urls.appRuntime} target="_blank" rel="noreferrer">{app.urls.appRuntime.replace(/^https?:\/\//, '')}</a>
            {app.urls.appRuntimeUat ? <> · UAT <a href={app.urls.appRuntimeUat} target="_blank" rel="noreferrer">{app.urls.appRuntimeUat.replace(/^https?:\/\//, '')}</a></> : null}
            {status?.state === 'running' ? ` · image ${status.image}` : ''}
            {probe ? ` · ${probe.ok ? 'answering' : `not answering (${probe.error || probe.status})`}` : ''}
          </>
        )}
        {rt.kind === 'container' && status?.state === 'failed' && (
          <> <span className="ap-slug-bad">Build failed: {status.error}</span> — fix it from the Dev Console and ship again, or save the settings below to retry.</>
        )}
        {rt.kind === 'container' && !form && (
          <> Its environment is the <code>app-env</code> secret (Dev Console → Secrets; <code>app-env-uat</code> for the UAT container): one KEY=value per line. Build args read their values from the same file.</>
        )}
      </p>
      {isAdmin && !form && (
        <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => setForm({ kind: rt.kind, dockerfile: rt.dockerfile || 'Dockerfile', port: rt.port || 3000, healthPath: rt.healthPath || '/', context: rt.context || '', buildArgs: (rt.buildArgs || []).join(', '), memory: rt.memory || '1g' })}>Change how it runs</button>
      )}
      {isAdmin && form && (
        <form onSubmit={save} data-agent-guard style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="cs-input" style={{ width: 220 }} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} aria-label="How the app runs">
            <option value="container">nventr runs it (Dockerfile)</option>
            <option value="downstream">Your pipeline runs it</option>
          </select>
          {form.kind === 'container' && (
            <>
              <input className="cs-input" style={{ width: 160 }} value={form.dockerfile} onChange={(e) => setForm({ ...form, dockerfile: e.target.value })} placeholder="Dockerfile" aria-label="Dockerfile" />
              <input className="cs-input" style={{ width: 90 }} value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} placeholder="port" aria-label="Container port" />
              <input className="cs-input" style={{ width: 140 }} value={form.healthPath} onChange={(e) => setForm({ ...form, healthPath: e.target.value })} placeholder="/health" aria-label="Health path" />
              <input className="cs-input" style={{ width: 150 }} value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} placeholder="build context (. = root)" aria-label="Build context" />
              <input className="cs-input" style={{ width: 240 }} value={form.buildArgs} onChange={(e) => setForm({ ...form, buildArgs: e.target.value })} placeholder="build arg names, comma-separated" aria-label="Build args (names; values from the app env file)" />
              <select className="cs-input" style={{ width: 90 }} value={form.memory} onChange={(e) => setForm({ ...form, memory: e.target.value })} aria-label="Memory limit">
                {['512m', '1g', '2g', '4g'].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </>
          )}
          <button type="submit" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy} data-agent-guard>{busy ? 'Applying…' : 'Apply'}</button>
          <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={busy} onClick={() => setForm(null)}>Cancel</button>
          {err && <span className="ap-slug-bad">{err}</span>}
        </form>
      )}
    </div>
  );
}

function AdoptionSection({ org, appId, app, isAdmin }) {
  const adoption = app?.adoption;
  const [domain, setDomain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!adoption?.source?.name) return null;
  const markReady = async () => {
    setBusy(true);
    setError('');
    try { await apiPost(`/apps/${org.id}/${appId}/adoption/ready`, {}); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const cutOver = async () => {
    setBusy(true);
    setError('');
    try { await apiPost(`/apps/${org.id}/${appId}/cutover`, { domain: domain.trim() }); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const legacy = app.monitor?.legacy;
  const keep = adoption.intent === 'keep';
  return (
    <section className="ap-section">
      <h2>Brought from {adoption.source.name}</h2>
      <p className="cs-help" style={{ marginTop: 0 }}>{verdictCopyOf(adoption)}</p>
      {keep && <RuntimeLine org={org} appId={appId} app={app} isAdmin={isAdmin} />}
      {adoption.legacyUrl && adoption.state !== 'cutover' && (
        <p className="cs-help">
          Still serving <a href={adoption.legacyUrl}>{adoption.legacyUrl}</a>
          {legacy ? ` · ${legacy.ok ? 'up' : 'not answering'}` : ''}
          . That site is watched on its own. It is not this instance.
        </p>
      )}
      {adoption.builderAppId && (
        <p className="cs-help">
          <Link to={`/orgs/${org.id}/builder?app=${adoption.builderAppId}`}>Open in App Builder</Link>
          {' '}— change requests and deploys run there and in the Dev Console.
          {' '}<Link to={`/orgs/${org.id}/builder?app=${adoption.builderAppId}&tab=lab&phase=stories&roadmap=1`}>Plan the roadmap</Link> from the documents you attached.
        </p>
      )}
      {(adoption.docs || (adoption.docIds || []).length > 0) && (
        <p className="cs-help">
          Project brain seeded with {adoption.docs?.attached ?? (adoption.docIds || []).length} attached document{(adoption.docs?.attached ?? (adoption.docIds || []).length) === 1 ? '' : 's'}
          {adoption.docs?.seeded ? ` and ${adoption.docs.seeded} page${adoption.docs.seeded === 1 ? '' : 's'} from the repository (README, docs, roadmap)` : ''}
          {adoption.docs?.error ? ` — repository docs: ${adoption.docs.error}` : ''}.
          {' '}<Link to={`/orgs/${org.id}/knowledge${adoption.builderAppId ? `?app=${adoption.builderAppId}` : ''}`}>Knowledge</Link>
        </p>
      )}
      {adoption.klass === 'foreign' && adoption.state === 'migrating' && (
        <p className="cs-help">The Dev Console has the original repository checked out read-only at <code>./source</code>. When the layer holds the app, mark it ready. The database does not move.</p>
      )}
      {isAdmin && adoption.klass === 'foreign' && adoption.state === 'migrating' && app.status === 'live' && (
        <button className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy} onClick={markReady}>The layer is ready</button>
      )}
      {isAdmin && adoption.state === 'live' && app.status === 'live' && (
        <div data-agent-guard style={{ marginTop: 8 }}>
          <p className="cs-help">
            {keep && adoption.runtime?.kind === 'container'
              ? `Cut over points your domain at your container (today at ${app.urls?.appRuntime || 'its nventr address'}); the console stays at ${appHost(app) || 'its nventr address'}. Point DNS when the page says so. Your database does not come with the domain.`
              : keep
                ? 'Your pipeline serves your domain already. Cut over here only records that the move is done so health and scans stop watching the old address.'
                : 'Cut over moves this instance onto your domain. Point DNS when the page says so. Your database does not come with the domain.'}
          </p>
          <div className="cs-field" style={{ maxWidth: 360 }}>
            <label>Apex domain</label>
            <input className="cs-input" placeholder="portal.example.com" value={domain} onChange={(e) => setDomain(e.target.value)} />
          </div>
          <button className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy || !domain.trim()} onClick={cutOver}>Cut over</button>
        </div>
      )}
      {adoption.state === 'cutover' && (
        <p className="cs-help">Cutover is in progress for {app.customDomain || domain || 'the domain'}. Your database does not move with it.</p>
      )}
      {error && <p className="cs-help ap-slug-bad">{error}</p>}
    </section>
  );
}

function BuilderDesignLine({ org, instance, canEdit }) {
  const { docs: designs } = useEntityList('app', { org: org.id }, { includeOrgWide: true });
  return (
    <p className="cs-help" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <span>App Builder design:</span>
      <DesignLinkSelect orgId={org.id} instance={instance} designs={designs && designs.filter((d) => !d.archived || d.links?.instanceAppId === instance?.id)} canEdit={canEdit} />
    </p>
  );
}

function AppDetail({ org, appId, myRole }) {
  const navigate = useNavigate();
  const can = useCan(org, myRole);
  const liveDoc = useOrgDoc('appInstance', 'apps', org.id, appId);
  const uatDoc = useOrgDoc('appInstance', 'apps', org.id, liveDoc?.uatAppId || null);
  const statements = useOrgDocs('invoiceStatement', 'statements', org.id);
  const acceptances = useOrgDocs('agreementAcceptance', 'acceptances', org.id);
  const [statusInfo, setStatusInfo] = useState(null); // { app, job }
  const [statusErr, setStatusErr] = useState('');
  const [retryBusy, setRetryBusy] = useState(false);
  const [retryErr, setRetryErr] = useState('');
  const [payDocs, setPayDocs] = useState(null);

  // The Firestore doc is the realtime source; the status endpoint fills in the
  // provisioning job (server-only collection). Poll every ~5s while work is in
  // flight, otherwise fetch once for the stage history.
  const app = liveDoc || statusInfo?.app || null;
  const status = app?.status;
  // awaiting-install polls too: the first license phone-home from a
  // customer-hosted install flips the app live server-side.
  const polling = !status || ['draft', 'awaiting_payment', 'provisioning', 'awaiting-install'].includes(status);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const data = await apiGet(`/apps/${org.id}/${appId}/status`);
        if (alive) {
          setStatusInfo(data);
          setStatusErr('');
        }
      } catch (e) {
        if (alive) setStatusErr(e.message);
      }
    };
    load();
    if (!polling) return () => { alive = false; };
    const t = setInterval(load, 5000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [org.id, appId, polling]);

  // Retry payment for drafts / awaiting_payment (e.g. after a canceled checkout).
  const retryCheckout = async () => {
    setRetryBusy(true);
    setRetryErr('');
    try {
      const out = await apiPost(`/apps/${org.id}/${appId}/checkout`);
      if (out.provisioned) {
        setRetryBusy(false);
        return; // status flips to provisioning via the live doc
      }
      window.location.href = out.url;
    } catch (e) {
      if (e.status === 409) {
        try {
          setPayDocs(await fetchAgreements());
          setRetryErr('');
        } catch (err) {
          setRetryErr(err.message);
        }
      } else {
        setRetryErr(e.message);
      }
      setRetryBusy(false);
    }
  };

  const job = statusInfo?.job || null;
  const cmd = useCommandToggle('app');
  const appStatements = (statements || []).filter((s) => s.appId === appId);
  const period = new Date().toISOString().slice(0, 7); // YYYY-MM (UTC)
  const currentDraft = appStatements.find((s) => s.period === period);
  const invoices = [...appStatements].sort((a, b) => (a.period < b.period ? 1 : -1));
  const myAcceptances = (acceptances || []).filter((a) => !a.appId || a.appId === appId);

  if (liveDoc === null && !statusInfo && statusErr) {
    return (
      <div>
        <p className="cs-help">
          App not found or you don&apos;t have access ({statusErr}).{' '}
          <Link to={`/orgs/${org.id}/apps`}>Back to apps</Link>
        </p>
      </div>
    );
  }

  return (
    <div>
      <p style={{ margin: '0 0 8px' }}>
        <Link to={`/orgs/${org.id}/apps`} className="cs-help" style={{ margin: 0 }}>← All apps</Link>
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>{app?.name || appId}</h1>
        {app && <StatusBadge status={app.status} />}
        <span style={{ flex: 1 }} />
        {status === 'live' && appUrl(app) && (
          <a className="cs-btn cs-btn-primary cs-btn-sm" href={appUrl(app)} target="_blank" rel="noreferrer">
            Open app
          </a>
        )}
        {status === 'live' && app?.urls?.dev && (
          <DevConsoleButton orgId={org.id} appId={appId} devUrl={app.urls.dev} siteMode={app.mode === 'site'} />
        )}
        {['draft', 'awaiting_payment'].includes(status) && (
          <button className="cs-btn cs-btn-primary cs-btn-sm" disabled={retryBusy} onClick={retryCheckout}>
            {retryBusy ? 'Redirecting…' : 'Complete payment'}
          </button>
        )}
        {status === 'live' && cmd.supported && <CommandToggle on={cmd.on} onChange={cmd.set} />}
      </div>
      {app && (
        <p className="cs-help">
          {isByo(app)
            ? (app.deployment.domain || `customer-hosted · ${DEPLOY_TARGETS.find((t) => t.key === app.deployment.target)?.name || app.deployment.target}`)
            : appHost(app)}
          {' · '}{APP_PLANS.find((p) => p.key === app.plan)?.name || app.plan}
          {app.region && !isByo(app) ? ` · ${app.region}` : ''}
          {app.prepaidUntil ? ` · prepaid until ${fmtDate(app.prepaidUntil)}` : ''}
        </p>
      )}
      {app && <WorkspaceLine org={org} app={app} appId={appId} isOrgAdmin={roleSatisfies(myRole, 'admin')} />}
      {app && app.envRole !== 'uat' && <BuilderDesignLine org={org} instance={app} canEdit={can('app.update')} />}
      {app?.adoption?.source?.name && (
        <AdoptionSection org={org} appId={appId} app={app} isAdmin={roleSatisfies(myRole, 'admin')} />
      )}
      {payDocs && (
        <div style={{ marginTop: 12 }}>
          <OrgAgree orgId={org.id} docs={payDocs} onDone={retryCheckout} />
        </div>
      )}
      {retryErr && <p className="cs-help ap-slug-bad">{retryErr}</p>}
      {app?.statusDetail && ['suspended', 'terminated'].includes(status) && (
        <div className="ap-banner is-warn">{app.statusDetail}</div>
      )}
      {status === 'live' && app?.mode === 'site' && (
        <div className="ap-banner is-warn" role="status" data-nova-note="This instance is parked in site mode: static public site only">
          <strong>Site mode.</strong> {SITE_MODE_NOTE}
          {app.siteMode?.since ? ` Since ${new Date(app.siteMode.since).toLocaleDateString()}.` : ''}
        </div>
      )}
      {status === 'live' && cmd.on && (
        <AppCommandView
          org={org}
          appId={appId}
          app={app}
          isAdmin={roleSatisfies(myRole, 'admin')}
          canChecks={can('instance.featureChecks')}
          currentDraft={currentDraft}
          invoices={invoices}
          period={period}
        />
      )}
      {status === 'live' && cmd.on && roleSatisfies(myRole, 'member') && (
        <ApSection id="ap-budget" title="Budget" meta="ask an admin to raise this app's monthly limit">
          <BudgetRequests org={org} myRole={myRole} appId={appId} app={app} />
        </ApSection>
      )}
      {!(status === 'live' && cmd.on) && (
      <>
      {status === 'live' && <SummaryStrip org={org} app={app} uatDoc={uatDoc} isAdmin={roleSatisfies(myRole, 'admin')} />}

      {/* Customer-hosted (BYO) install kit + phone-home state */}
      {isByo(app) && ['awaiting-install', 'live'].includes(status) && (
        <section className="ap-section">
          <h2>Your deployment</h2>
          <ByoInstallSection org={org} appId={appId} app={app} license={statusInfo?.license || null} />
        </section>
      )}

      {/* Provisioning timeline — folded away once the app is live */}
      <ApSection id="ap-provisioning" title="Provisioning" defaultOpen={status !== 'live'} meta={status === 'live' ? 'complete' : null}>
        {statusErr && !statusInfo && <p className="cs-help">Status unavailable: {statusErr}</p>}
        {job && (job.stages || []).length > 0 ? (
          <ol className="ap-stages">
            {job.stages.map((st) => (
              <li key={st.name} className="ap-stage">
                <StageIcon status={st.status} />
                <span className="ap-stage-name">{st.name}</span>
                {st.at && <span className="cs-time">{new Date(st.at).toLocaleTimeString()}</span>}
                {st.detail && STAGE_ERR.includes(String(st.status || '').toLowerCase()) && (
                  <span className="ap-stage-detail">{st.detail}</span>
                )}
              </li>
            ))}
          </ol>
        ) : (
          <p className="cs-help">
            {status === 'live'
              ? 'Provisioning complete.'
              : polling
                ? 'Waiting for provisioning to start…'
                : 'No provisioning job on record.'}
          </p>
        )}
        {job?.status && STAGE_ERR.includes(String(job.status).toLowerCase()) && app?.statusDetail && (
          <div className="ap-banner is-err">{humanizeStatusDetail(app.statusDetail, job)}</div>
        )}
      </ApSection>

      {/* Live picture — the scene: monitor, twin, scans, edge, updates; the service list is /live/services */}
      {status === 'live' && (
        <ApSection id="ap-eco" title="Live picture" className="ap-eco" meta="the instance as it runs — services, monitor, security, twin; the service list is under Services">
          <EcosystemSection orgId={org.id} appId={appId} />
        </ApSection>
      )}

      {/* Health (monitor worker) */}
      {status === 'live' && (
        <ApSection id="ap-health" title="Health" meta="uptime, latency, TLS, your feature checks">
          <HealthSection orgId={org.id} appId={appId} canEdit={can('instance.featureChecks')} />
        </ApSection>
      )}

      {/* Security posture (Assurance: scans + findings) */}
      {status === 'live' && (
        <ApSection id="ap-security" title="Security" meta={app?.assurance ? 'passive baseline · ZAP, Nuclei, TLS · findings ledger' : 'Assurance add-on'}>
          <SecuritySection orgId={org.id} appId={appId} isAdmin={roleSatisfies(myRole, 'admin')} />
        </ApSection>
      )}

      {/* Dev Console access — instance operator accounts (separate sign-in) */}
      {status === 'live' && !isByo(app) && app?.urls?.dev && (
        <ApSection id="ap-devaccess" title="Dev Console access" meta="instance sign-in · mint or rotate operator accounts">
          <DevAccessSection orgId={org.id} appId={appId} app={app} />
        </ApSection>
      )}

      {/* Keys from the Vault — Administration → Vault entries bound to this instance */}
      {status === 'live' && (app?.envRole || 'prod') === 'prod' && !isByo(app) && (
        <ApSection id="ap-vault" title="Keys from the Vault" meta="customer secrets placed by the platform · re-placed after every re-stamp" defaultOpen={false}>
          <VaultKeysSection orgId={org.id} appId={appId} app={app} />
        </ApSection>
      )}

      {/* UAT environment (add-on) — managed prod instances only */}
      {status === 'live' && (app?.envRole || 'prod') === 'prod' && !isByo(app) && (
        <ApSection id="ap-envs" title="Environments" meta="production + UAT twin, deploys">
          <UatSection org={org} appId={appId} app={app} isAdmin={roleSatisfies(myRole, 'admin')} canEdit={can('instance.uat')} />
        </ApSection>
      )}

      {/* Email service — mailboxes on the app's domain + /mail inbox (shared plane included, dedicated add-on) */}
      {status === 'live' && (app?.envRole || 'prod') === 'prod' && (
        <ApSection id="ap-mail" title="Email" meta={app?.mail?.status && app.mail.status !== 'off' ? `${app.mail.domain || ''} · ${app.mail.status}` : 'mailboxes on your domain · inbox at /mail'} defaultOpen={!!app?.mail?.status && app.mail.status !== 'off'}>
          <MailSection orgId={org.id} appId={appId} app={app} isAdmin={roleSatisfies(myRole, 'admin')} />
        </ApSection>
      )}

      {/* Actual AWS costs (org admins). Members get the request form in place of it. */}
      {['live', 'suspended'].includes(status) && (
        <CostsSection orgId={org.id} appId={appId} />
      )}
      {['live', 'suspended'].includes(status) && roleSatisfies(myRole, 'member') && (
        <ApSection id="ap-budget" title="Budget" meta="ask an admin to raise this app's monthly limit">
          <BudgetRequests org={org} myRole={myRole} appId={appId} app={app} />
        </ApSection>
      )}

      {/* Usage this month */}
      <ApSection id="ap-usage" title={`Usage · ${period}`} defaultOpen={false}>
        <UsageSection orgId={org.id} appId={appId} currentDraft={currentDraft} />
      </ApSection>

      {/* Invoices */}
      <ApSection id="ap-invoices" title="Invoices" defaultOpen={false} meta={invoices.length ? `${invoices.length}` : 'none yet'}>
        {!invoices.length && <p className="cs-help">No invoices yet — the first statement lands after month 1.</p>}
        {invoices.length > 0 && (
          <div className="cs-plan-tablewrap">
            <table>
              <thead>
                <tr><th>Period</th><th>Lines</th><th>Subtotal</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td>{inv.period}</td>
                    <td>{(inv.lines || []).length}</td>
                    <td>{fmtUsd(inv.subtotalCents)}</td>
                    <td>{inv.status || '—'}</td>
                    <td>
                      {inv.stripeInvoiceId && (
                        <a
                          href={`https://dashboard.stripe.com/invoices/${inv.stripeInvoiceId}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Stripe invoice →
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ApSection>

      {/* Agreements */}
      <ApSection id="ap-agreements" title="Agreements" defaultOpen={false}>
        {!myAcceptances.length && <p className="cs-help">No accepted agreements on record.</p>}
        <div style={{ display: 'grid', gap: 8 }}>
          {myAcceptances.map((a) => (
            <div key={a.id} className="cs-card" style={{ padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'center' }}>
              <strong style={{ color: 'var(--text)' }}>{a.title || a.kind || a.docId || a.id}</strong>
              {a.version && <span className="cs-help" style={{ margin: 0 }}>v{a.version}</span>}
              <span style={{ flex: 1 }} />
              <span className="cs-time">
                {fmtDate(a.acceptedAt || a.createdAt) || ''}
              </span>
            </div>
          ))}
        </div>
      </ApSection>

      {/* How the kit works — customer-facing explainer (public deep-dive
          lives at nventr.io/app-kit; operator view at /admin/apps → IP map). */}
      <ApSection id="ap-how" title="How your app is built" defaultOpen={false}>
        <div style={{ display: 'grid', gap: 8 }}>
          <p className="cs-help" style={{ margin: 0 }}>
            <strong style={{ color: 'var(--text)' }}>Your code layer.</strong>{' '}
            Everything built for this app lives in its own repo
            {app?.repo?.name ? <> (<code>{app.repo.name}</code>)</> : null} that
            you own — pages, workers, data entities. The nventr App Kit
            underneath is rented at a pinned release and never enters your
            repo; a lint gate enforces the boundary on every deploy.
          </p>
          <p className="cs-help" style={{ margin: 0 }}>
            <strong style={{ color: 'var(--text)' }}>The Dev Console.</strong>{' '}
            AI agents at <code>{app?.urls?.dev || '/dev'}</code> work your
            layer only: describe a change, review the diff, deploy. The kit is
            a read-only reference for them — a commit can never contain kit code.
          </p>
          <p className="cs-help" style={{ margin: 0 }}>
            <strong style={{ color: 'var(--text)' }}>Export rights.</strong>{' '}
            Your layer exports as a complete git bundle on request, any time —
            full history, no kit code inside, no exit negotiation.
          </p>
          <p className="cs-help" style={{ margin: 0 }}>
            <strong style={{ color: 'var(--text)' }}>License &amp; usage.</strong>{' '}
            The instance validates its license key with nventr every 12h and
            reports usage counters (AI tokens, storage, egress — never your
            content) every 6h, per your agreement. A lapsed license pauses the
            platform; your repo and database remain yours.
          </p>
          <p className="cs-help" style={{ margin: 0 }}>
            Full write-up:{' '}
            <a href="https://nventr.io/app-kit" target="_blank" rel="noreferrer">
              nventr.io/app-kit →
            </a>
          </p>
        </div>
      </ApSection>

      {/* Team */}
      <ApSection id="ap-team" title="Team">
        <p className="cs-help">
          Access to this app follows your organization&apos;s membership —{' '}
          <Link to={`/orgs/${org.id}/members`} onClick={(e) => { e.preventDefault(); navigate(`/orgs/${org.id}/members`); }}>
            manage members →
          </Link>
        </p>
      </ApSection>
      </>
      )}
    </div>
  );
}

// BYO install panel: generates the per-target install kit (bootstrap script
// with the license + usage keys embedded — shown ONCE; regenerating rotates
// the license key) and tracks the instance's phone-home state.
function ByoInstallSection({ org, appId, app, license }) {
  const [domain, setDomain] = useState(app?.deployment?.domain || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [kit, setKit] = useState(null); // { script, notes, termEnd, target }
  const target = app?.deployment?.target;
  const targetName = DEPLOY_TARGETS.find((t) => t.key === target)?.name || target;
  const phoned = !!license?.lastSeenAt;

  const generate = async () => {
    setBusy(true);
    setErr('');
    try {
      const out = await apiPost(`/apps/${org.id}/${appId}/install-kit`, { domain: domain.trim() });
      setKit(out);
    } catch (e) {
      setErr(e.message);
    }
    setBusy(false);
  };

  const download = () => {
    const blob = new Blob([kit.script], { type: 'text/x-shellscript' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `install-${app.slug}-${target}.sh`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div>
      <p className="cs-help" style={{ marginTop: 0 }}>
        Target: <strong>{targetName}</strong>.{' '}
        {phoned ? (
          <>Instance is phoning home — last license check {fmtDate(license.lastSeenAt)}{license.lastVersion ? ` (kit v${license.lastVersion})` : ''}.</>
        ) : (
          <>Waiting for the first phone-home: run the install kit on your server and this page flips live automatically.</>
        )}
      </p>
      <div className="cs-field" style={{ maxWidth: 420 }}>
        <label>Your domain (the DNS name your server will answer on)</label>
        <input
          className="cs-input"
          placeholder="app.yourcompany.com"
          value={domain}
          onChange={(e) => setDomain(e.target.value.toLowerCase())}
        />
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy} onClick={generate}>
          {busy ? 'Generating…' : kit || app.licenseId ? 'Regenerate install kit (rotates license key)' : 'Generate install kit'}
        </button>
        {kit && (
          <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={download}>
            Download install-{app.slug}-{target}.sh
          </button>
        )}
      </div>
      {err && <p className="cs-help ap-slug-bad">{err}</p>}
      {kit && (
        <>
          <div className="ap-banner is-warn" style={{ marginTop: 10 }}>
            The license and usage keys are embedded in this script and shown only
            once — store it securely. Regenerating rotates the license key and the
            previous one stops validating.
          </div>
          <textarea
            className="cs-input"
            readOnly
            value={kit.script}
            style={{ width: '100%', minHeight: 220, marginTop: 10, fontFamily: 'monospace', fontSize: 12 }}
          />
          {kit.notes && <p className="cs-help">{kit.notes}</p>}
        </>
      )}
    </div>
  );
}

// Command view for one live instance: the command feed (ecosystem + health
// history + usage + statement + license + costs for admins) drives the
// scene and the HUD; the drawer opens the SAME sections the classic layout
// renders. Polls every 30s while visible.
function AppCommandView({ org, appId, app, isAdmin, canChecks = isAdmin, currentDraft, invoices, period }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Feed + telemetry + live cards: shared with the full-page room.
  const { data, err, state, telemetry, controls, inspectorFor, columns, live, feed: liveFeed } = useAppCommand({ orgId: org.id, appId, app });
  // The live layer on the in-page stage: request beads, vitals, deploy scaffolding (no presence, no replay — that is the room's).
  const wire = useAppWire({ orgId: org.id, appId, live, feed: liveFeed, enabled: !!data, replay: false });
  const hostLabel = app?.host ? `…${String(app.host).slice(-6)}${app?.region ? ` · ${app.region}` : ''}` : (isByo(app) ? 'customer-hosted' : app?.region || null);
  const panels = useMemo(() => {
    if (!data) return [];
    const raw = [...appPanels(state, { isAdmin, hostLabel }), trafficPanel(telemetry, { side: 'left', id: 'ap-traffic' }), anomalyPanel(telemetry, { side: 'right', id: 'ap-anomalies' })];
    return raw.map((p) => (APP_ASPECT[p.id] ? { ...p, onOpen: () => navigate(appLive(org.id, appId, APP_ASPECT[p.id])) } : p));
  }, [data, state, isAdmin, hostLabel, telemetry, navigate, org.id, appId]);
  const onOpenDetail = useCallback((id) => {
    if (!APP_ASPECT[id]) return false;
    navigate(appLive(org.id, appId, APP_ASPECT[id]));
    return true;
  }, [navigate, org.id, appId]);
  const drawer = useMemo(() => ({
    'ap-traffic': { title: `Traffic & flows · ${telemetry.window.label}`, render: () => <FlowTable t={telemetry} /> },
    'ap-anomalies': { title: `Anomalies · ${telemetry.window.label}`, render: () => <AnomalyTable t={telemetry} /> },
    'ap-identity': { title: 'Provisioning & access', render: () => (<><ProvisioningDetail org={org} appId={appId} /><DevAccessSection orgId={org.id} appId={appId} app={app} /></>) },
    'ap-health': { title: 'Health', render: () => <HealthSection orgId={org.id} appId={appId} canEdit={canChecks} /> },
    'ap-services': { title: 'Live picture', render: () => <EcosystemSection orgId={org.id} appId={appId} /> },
    'ap-eco': { title: 'Live picture', render: () => <EcosystemSection orgId={org.id} appId={appId} /> },
    'ap-security': { title: 'Security', render: () => <SecuritySection orgId={org.id} appId={appId} isAdmin={isAdmin} /> },
    'ap-envs': { title: 'Environments', render: () => <UatSection org={org} appId={appId} app={app} isAdmin={isAdmin} /> },
    'ap-devaccess': { title: 'Dev Console access', render: () => <DevAccessSection orgId={org.id} appId={appId} app={app} /> },
    'ap-vault': { title: 'Keys from the Vault', render: () => <VaultKeysSection orgId={org.id} appId={appId} app={app} /> },
    'ap-mail': { title: 'Email', render: () => <MailSection orgId={org.id} appId={appId} app={app} isAdmin={isAdmin} /> },
    'ap-costs': { title: `Costs · ${period}`, render: () => <CostsSection orgId={org.id} appId={appId} /> },
    'ap-usage': { title: `Usage · ${period}`, render: () => (<>{currentDraft && <StatementLines statement={currentDraft} />}<UsageCounters usage={data?.usage} /></>) },
    'ap-invoices': { title: 'Invoices', render: () => <InvoicesTable invoices={invoices} /> },
  }), [org, appId, app, isAdmin, canChecks, period, currentDraft, invoices, telemetry, data]);

  return (
    <CommandView
      data={data}
      derive={deriveCommand}
      scene="command"
      panels={panels}
      drawer={drawer}
      events={state.events || []}
      caption={data ? state.caption : (err ? `Command feed: ${err}` : 'loading…')}
      chrome={184}
      telemetry={telemetry}
      defaultInspector={data ? inspectorFor(null) : null}
      inspectorFor={inspectorFor}
      columns={columns}
      onOpenDetail={onOpenDetail}
      roomHref={`/orgs/${org.id}/apps/${appId}/room`}
      initialSelected={location.state?.roomSel || null}
      wire={wire}
    >
      <div className="cmd-rail-actions">
        {appUrl(app) && <a className="cs-btn cs-btn-sm" href={appUrl(app)} target="_blank" rel="noreferrer">Open app</a>}
        {app?.urls?.dev && <DevConsoleButton orgId={org.id} appId={appId} devUrl={app.urls.dev} className="cs-btn cs-btn-sm" label="Dev Console" siteMode={app.mode === 'site'} />}
        <a className="cs-btn cs-btn-sm" href="#ap-invoices">Invoices{invoices.length ? ` · ${invoices.length}` : ''}</a>
      </div>
      <TelemetryControls controls={controls} compact />
      <ActivityStrip telemetry={telemetry} />
    </CommandView>
  );
}

function ProvisioningDetail({ org, appId }) {
  const [info, setInfo] = useState(null);
  useEffect(() => { apiGet(`/apps/${org.id}/${appId}/status`).then(setInfo).catch(() => setInfo(null)); }, [org.id, appId]);
  const job = info?.job;
  if (!job?.stages?.length) return <p className="cs-help">Provisioning complete.</p>;
  return (
    <ol className="ap-stages" style={{ marginBottom: 20 }}>
      {job.stages.map((st) => (
        <li key={st.name} className="ap-stage">
          <StageIcon status={st.status} />
          <span className="ap-stage-name">{st.name}</span>
          {st.at && <span className="cs-time">{new Date(st.at).toLocaleTimeString()}</span>}
        </li>
      ))}
    </ol>
  );
}

function InvoicesTable({ invoices }) {
  if (!invoices.length) return <p className="cs-help">No invoices yet — the first statement lands after month 1.</p>;
  return (
    <div className="cs-plan-tablewrap">
      <table>
        <thead><tr><th>Period</th><th>Lines</th><th>Subtotal</th><th>Status</th><th /></tr></thead>
        <tbody>
          {invoices.map((inv) => (
            <tr key={inv.id}>
              <td>{inv.period}</td>
              <td>{(inv.lines || []).length}</td>
              <td>{fmtUsd(inv.subtotalCents)}</td>
              <td>{inv.status || '—'}</td>
              <td>{inv.stripeInvoiceId && <a href={`https://dashboard.stripe.com/invoices/${inv.stripeInvoiceId}`} target="_blank" rel="noreferrer">Stripe invoice →</a>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Live usage counters for the month (instanceUsage/{slug}/months/{m}, phoned
// home hourly by the kit). The statement is only drafted at month close, so
// this is what "usage" means for the current period.
const USAGE_METERS = [
  ['tokens', 'AI tokens', 'n'], ['aiTurns', 'AI turns', 'n'], ['agentRuns', 'Agent runs', 'n'],
  ['agentConversations', 'Agent conversations', 'n'], ['pipelineRuns', 'Pipeline runs', 'n'], ['jobRuns', 'Job runs', 'n'],
  ['voiceSessions', 'Voice sessions', 'n'], ['voiceMinutes', 'Voice minutes', 'n'], ['devCents', 'Dev Console', 'cents'],
  ['storageBytes', 'Storage (high-water)', 'bytes'], ['ingressBytes', 'Ingress', 'bytes'], ['egressBytes', 'Egress', 'bytes'],
  ['computeMs', 'Compute', 'hours'], ['gpuMs', 'GPU', 'hours'],
];
const TOKEN_SURFACES = [['tokens_copilot', 'copilot'], ['tokens_voice', 'voice'], ['runTokens', 'runs'], ['tokens_embed', 'embed'], ['tokens_other', 'other']];
const fmtBytes = (b) => (b < 1e6 ? `${(b / 1e3).toFixed(0)} KB` : b < 1e9 ? `${(b / 1e6).toFixed(1)} MB` : `${(b / 1e9).toFixed(2)} GB`);
// Token spend is small per model; keep sub-dime amounts legible.
const fmtTokUsd = (v) => (!(v > 0) ? '$0.00' : v >= 0.1 ? `$${v.toFixed(2)}` : v < 0.00005 ? '<$0.0001' : `$${v.toFixed(4)}`);
const fmtMeter = (v, unit) => (unit === 'bytes' ? fmtBytes(v) : unit === 'cents' ? fmtUsd(v) : unit === 'hours' ? `${(v / 3600e3).toFixed(2)} h` : v.toLocaleString());

function UsageCounters({ usage }) {
  const c = usage?.counters || {};
  const rows = USAGE_METERS.filter(([k]) => Number(c[k]) > 0);
  if (!rows.length) {
    return <p className="cs-help">{usage?.reportedAt ? 'The instance has reported, but no metered activity yet this month.' : 'No usage report from this instance yet this month — the kit phones counters home hourly.'}</p>;
  }
  const surfaces = TOKEN_SURFACES.filter(([k]) => Number(c[k]) > 0).map(([k, l]) => `${l} ${Number(c[k]).toLocaleString()}`).join(' · ');
  // Priced server-side (shared/costs.js priceModelTokens: list prices from
  // models.priceCard × tokens) so the card and the Costs section agree.
  // Row/total cost from the unrounded per-direction figures, so the columns
  // add up on screen (the server's `usd` is rounded to cents for the ledger).
  const rowUsd = (m) => (m.inUsd || 0) + (m.outUsd || 0) + (m.cacheUsd || 0);
  const models = Object.values(usage?.model?.byModel || {}).sort((a, b) => rowUsd(b) - rowUsd(a));
  const totalUsd = models.reduce((a, m) => a + rowUsd(m), 0);
  const hasCache = models.some((m) => m.cacheTokens > 0);
  const tok = (n, usd) => <>{(n || 0).toLocaleString()}<span className="cs-help" style={{ margin: 0, display: 'block' }}>{fmtTokUsd(usd)}</span></>;
  const users = usage?.byUser || [];
  return (
    <div className="cs-plan-tablewrap">
      <table>
        <thead><tr><th>Meter</th><th>This month</th></tr></thead>
        <tbody>
          {rows.map(([k, label, unit]) => (
            <tr key={k}>
              <td>{label}{k === 'tokens' && surfaces ? <span className="cs-help" style={{ margin: 0, display: 'block' }}>{surfaces}</span> : null}</td>
              <td>{fmtMeter(Number(c[k]), unit)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {models.length > 0 && (
        <table style={{ marginTop: 10 }}>
          <thead><tr><th>Model</th><th>In</th><th>Out</th>{hasCache && <th>Cache</th>}<th>Cost</th></tr></thead>
          <tbody>
            {models.map((m) => (
              <tr key={m.key}>
                <td>{m.key}{!m.priced && <span className="cs-help" style={{ margin: 0, display: 'block' }}>not in price card · default rate</span>}</td>
                <td>{tok(m.inTokens, m.inUsd)}</td>
                <td>{tok(m.outTokens, m.outUsd)}</td>
                {hasCache && <td>{tok(m.cacheTokens, m.cacheUsd)}</td>}
                <td>{fmtTokUsd(rowUsd(m))}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={hasCache ? 4 : 3}><strong>Token cost (estimated)</strong><span className="cs-help" style={{ margin: 0, display: 'block' }}>list prices × tokens — the provider bill is the actual</span></td>
              <td><strong>{fmtTokUsd(totalUsd)}</strong></td>
            </tr>
          </tbody>
        </table>
      )}
      {users.length > 0 && (
        <table style={{ marginTop: 10 }}>
          <thead><tr><th>Top users</th><th>Tokens</th><th>Turns</th></tr></thead>
          <tbody>
            {users.map((u) => <tr key={u.uid}><td>{u.uid === 'other' ? 'everyone else' : u.uid}</td><td>{(u.tokens || 0).toLocaleString()}</td><td>{u.aiTurns || 0}</td></tr>)}
          </tbody>
        </table>
      )}
      {usage?.reportedAt && <p className="cs-help" style={{ marginTop: 8 }}>Reported {fmtAgo(usage.reportedAt)} · the invoice statement is drafted at month close.</p>}
    </div>
  );
}

// Classic section: fetches the counters itself (the command feed carries
// them for the drawer).
export function UsageSection({ orgId, appId, currentDraft }) {
  const [usage, setUsage] = useState(undefined);
  useEffect(() => {
    let alive = true;
    const load = () => {
      if (document.visibilityState !== 'visible') return;
      apiGet(`/apps/${orgId}/${appId}/usage`).then((u) => alive && setUsage(u)).catch(() => alive && usage === undefined && setUsage(null));
    };
    load();
    const t = setInterval(load, 60000); // audit P1-2: poll like Health
    document.addEventListener('visibilitychange', load);
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', load); };
  }, [orgId, appId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      {currentDraft && <StatementLines statement={currentDraft} />}
      {usage === undefined ? <p className="cs-help">Loading usage…</p> : <UsageCounters usage={usage} />}
    </>
  );
}

function StatementLines({ statement }) {
  const lines = statement.lines || [];
  if (!lines.length) return <p className="cs-help">Statement open — no metered lines yet.</p>;
  return (
    <div className="cs-plan-tablewrap">
      <table>
        <thead>
          <tr><th>Item</th><th>Qty</th><th>Included</th><th>Rate</th><th>Amount</th></tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.key}>
              <td>{l.label || l.key}</td>
              <td>{l.qty}{l.unit ? ` ${l.unit}` : ''}</td>
              <td>{l.includedQty ?? '—'}</td>
              <td>{l.rateCents != null ? fmtUsd(l.rateCents) : '—'}</td>
              <td>{fmtUsd(l.amountCents)}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={4}><strong>Subtotal (draft)</strong></td>
            <td><strong>{fmtUsd(statement.subtotalCents)}</strong></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
